import type { Dictionary } from "@/lib/i18n/dictionaries/en";

const zh: Dictionary = {
  // ---- /citation-styles/alphabetical/<letter> ----
  styleDirectory: {
    notFoundTitle: "未找到引用格式目录",
    metaTitle: "以 {label} 开头的引用格式",
    metaDescription:
      "浏览 {count} 种以 {label} 开头的 Citation Style Language 格式。打开任一格式即可查看真实引用示例和免费生成器。",
    ogSubtitle: "{count} 种格式，均配有专属示例和生成器。",
    ogKind: "A–Z 目录",
    schemaName: "以 {label} 开头的引用格式",
    schemaDescription:
      "{count} 种以 {label} 开头的 Citation Style Language 格式的字母顺序目录。",
    breadcrumbStyles: "引用格式",
    heading: "以 {label} 开头的引用格式",
    intro:
      "浏览 CSL 目录中此部分的全部 {count} 种格式。每个页面都包含该格式自身的排版示例和免费引用生成器。",
    navLabel: "引用格式 A 到 Z",
    listHeading: "所有以 {label} 开头的格式",
  },
  nav: {
    tools: "工具",
    guides: "指南",
    styles: "全部样式",
    bibliography: "我的参考文献",
    search: "搜索",
    glossary: "术语表",
    home: "首页",
    menu: "菜单",
  },

  home: {
    title: "免费参考文献生成器",
    subtitle: "2026 年更新",
    intro:
      "粘贴 DOI、网址或 BibTeX 条目，或直接搜索图书，即可获得符合 APA、MLA、芝加哥、温哥华、哈佛等数千种样式的规范参考文献。完全免费，无需付费订阅，无需注册。",
    browseAllStyles: "浏览全部 {count} 种引用样式 →",
    featuresHeading: "引用所需的一切",
    featureStyles: "{count} 种样式",
    featureStylesDetail: "APA、MLA、芝加哥等",
    featureBibliographies: "多个文献列表",
    featureBibliographiesDetail: "按课题分开整理",
    featureBookSearch: "图书检索",
    featureBookSearchDetail: "按书名或作者",
    featureExport: "随处导出",
    featureExportDetail: "BibTeX、Word、RIS、CSV",
    featureDarkMode: "深色模式",
    featureDarkModeDetail: "护眼舒适",
    featureNoSignup: "无需注册",
    featureNoSignupDetail: "永久免费，无需付费订阅",
    aboutHeading: "面向学生与研究者的免费参考文献生成器",
    aboutBody:
      "CitationEasy 支持一万余种学术引用样式。无论是心理学的 APA、文学的 MLA、历史学的芝加哥格式，还是医学期刊的温哥华格式，排版都直接取自该样式的官方定义，而非近似模仿。",
    howItWorksHeading: "使用方法",
    howItWorksStep1:
      "粘贴 DOI、网址、ISBN、PubMed 或 arXiv 编号，或 BibTeX 条目；也可以按书名检索图书。",
    howItWorksStep2:
      "选择引用样式。输入关键词即可在全部 {count} 种样式中搜索。",
    howItWorksStep3:
      "复制生成好的参考文献，或保存到文献列表后导出为 Word、BibTeX 或 RIS。",
    privacyNote:
      "您的文献列表保存在自己的浏览器中。无需账号，也不会上传到任何服务器。",
  },

  hubs: {
    toolsTitle: "免费的写作与引用工具",
    toolsIntro:
      "{count} 款在浏览器中直接运行的写作与引用工具：涵盖各类文献的生成器、字数与字符统计、可读性评估、排版工具和格式转换器。全部免费，即开即用。",
    stylesTitle: "浏览全部引用样式",
    stylesIntro:
      "Citation Style Language 项目中的全部 {count} 种样式，可按国家、学科和研究领域检索与筛选。",
    guidesTitle: "引用与学术写作指南",
    guidesIntro:
      "关于如何引用各类文献、如何评估检索到的资料，以及如何把文章写得更好的详细指南。",
    howToCiteTitle: "如何引用任何文献",
    howToCiteIntro:
      "分步骤页面，将每种常见文献类型与主流引用样式一一对应，每页都附有真实示例。",
    faqTitle: "引用常见问题",
    faqIntro: "针对学生在引用时真正会遇到的问题，给出简明准确的解答。",
    glossaryTitle: "引用术语表",
    glossaryIntro: "用通俗语言解释样式手册和作业要求中出现的术语。",
    openHub: "打开",
  },

  styleLanding: {
    metaTitle: "免费 {style} 参考文献生成器",
    metaDescription:
      "免费生成 {style} 引用。粘贴 DOI、网址、ISBN 或 BibTeX，即可获得符合 {style} 规范的参考文献条目与文内引用。",
    heading: "{style} 参考文献生成器",
    intro:
      "一键为图书、期刊论文和网页生成准确的 {style} 引用。免费，无需注册。",
    aboutHeading: "关于 {style}",
    whenToUseHeading: "何时使用 {style}",
    factsHeading: "{style} 一览",
    factEdition: "版本",
    factSystem: "引用体系",
    factInText: "文内格式",
    factReferenceList: "文献表名称",
    factFields: "常用于",
    systemAuthorDate: "著者—出版年制",
    systemNumbered: "顺序编码制",
    systemFootnotes: "脚注制",
    howToHeading: "如何使用这个 {style} 生成器",
    howToStep1:
      "在上方输入框中粘贴网址、DOI、ISBN 或 BibTeX 条目，也可以按书名检索图书。",
    howToStep2: "本页已经预先选好 {style}，可以直接生成。",
    howToStep3: "把结果复制到文档中，或先保存下来，之后再导出整份文献列表。",
    relatedHeading: "更多 {style} 相关资源",
    englishOnlyNote: "更详细的 {style} 指南与按文献类型分类的示例提供英文版。",
  },

  // The per-style pages at `/<locale>/citation-style/<slug>` — one per catalog style.
  //
  // The formatted examples the page is built around (the style's own output for a
  // fixed journal article, book and website) and the style's own name stay in
  // `src/data/citation-styles.ts` and `src/data/style-examples-generated.json`. They
  // are language-neutral: a citation rendered by the style's own CSL definition looks
  // the same whatever language the reader speaks, and "translating" one would make the
  // page wrong.
  //
  // The rest of what `src/data/**` supplies to these pages is NOT like that — see the
  // `generic*`, `discipline*` and `internationalLabel` keys below.
  citationStylePage: {
    notFoundTitle: "未找到该引用样式",
    metaTitle: "{style} 参考文献生成器 - 免费 {style} 格式",
    metaFormatAndFields: "{style} 是一种{format}体系，常用于{fields}领域。",
    metaFormatOnly: "{style} 是一种{format}体系。",
    metaFieldsOnly: "{style} 常用于{fields}领域。",
    metaPromiseNote: "查看真实的 {style} 脚注示例，并免费生成你自己的引用。",
    metaPromiseExamples:
      "查看期刊论文、图书和网页的真实 {style} 引用示例，并免费生成你自己的引用。",
    ogKindFormat: "{format} 引用样式",
    ogKindDefault: "引用样式",
    // Joins the style's discipline list: "psychology, education and nursing".
    disciplineConjunction: "和",
    // ---- Data-derived facts that are nonetheless mail-merged prose ----
    //
    // Every entry in `src/data/citation-styles-generated.ts` carries exactly the
    // two sentences below with their own name spliced in, exactly one region value
    // ("International" — that is `internationalLabel` above, reused rather than
    // duplicated), and discipline tokens drawn from a closed 26-word vocabulary. None
    // of that is a citation, so none of it is language-neutral the way the formatted
    // examples are. Left in `src/data/**` it renders English facts inside translated
    // chrome, which is precisely the hybrid page this tree exists to avoid.
    //
    // The page substitutes the two templates ONLY when a style's stored text is
    // byte-identical to the English template with its own name interpolated, so the
    // ~100 hand-curated styles keep their bespoke English descriptions instead of
    // being flattened into a generic sentence. `{name}` is the style's full name.
    genericDescription: "{name}的引用样式，来自 Citation Style Language 仓库。",
    genericWhenUsed: "用于向采用 {name} 样式的出版物投稿，或在其中进行引用。",

    // The 26 discipline tokens `style-examples-generated.json` uses. Each English
    // value is the exact string the page printed before these keys existed — the raw
    // token with hyphens turned into spaces — so `generic-base` reads "generic base"
    // and `political_science` keeps its underscore. An unknown token (a new one from
    // upstream CSL) falls back to that same prettified form rather than to a key name.
    disciplineAnthropology: "人类学",
    disciplineAstronomy: "天文学",
    disciplineBiology: "生物学",
    disciplineBotany: "植物学",
    disciplineChemistry: "化学",
    disciplineCommunications: "传播学",
    disciplineEngineering: "工程学",
    disciplineGenericBase: "通用",
    disciplineGeography: "地理学",
    disciplineGeology: "地质学",
    disciplineHistory: "历史学",
    disciplineHumanities: "人文学科",
    disciplineLaw: "法学",
    disciplineLinguistics: "语言学",
    disciplineLiterature: "文学",
    disciplineMath: "数学",
    disciplineMedicine: "医学",
    disciplinePhilosophy: "哲学",
    disciplinePhysics: "物理学",
    disciplinePoliticalScience: "政治学",
    disciplinePsychology: "心理学",
    disciplineScience: "科学",
    disciplineSocialScience: "社会科学",
    disciplineSociology: "社会学",
    disciplineTheology: "神学",
    disciplineZoology: "动物学",

    // Plain-language descriptions of the citation-format classes the CSL definitions
    // declare. `{format}` in the fallback is the raw class name from the data.
    formatClassAuthorDate:
      "著者—出版年制：文内引用标注作者姓名和出版年份，文献表按作者姓氏的字母顺序排列",
    formatClassNumeric:
      "顺序编码制：各文献按首次被引用的顺序编号，文内引用使用对应的编号",
    formatClassNote:
      "脚注制：引用以脚注或尾注的形式出现，而不是正文括号中的标注",
    formatClassLabel: "标签制：文内引用使用由作者姓名和年份构成的简短标签",
    formatClassAuthor: "著者制：文内引用只标注作者姓名，完整信息见文献表",
    formatClassOther: "{format} 体系",
    breadcrumbStyles: "引用样式",
    internationalLabel: "国际通用",
    heading: "{style} 参考文献生成器",
    intro: "一键为图书、论文和网页生成准确的 {style} 引用。",
    examplesHeading: "{style} 引用示例",
    examplesIntro: "同样的三种文献，按 {style} 格式排版：",
    examplesIntroWithFormat: "同样的三种文献，按 {style} 格式排版——{format}：",
    examplesNoteOnlyIntro:
      "{style} 是一种脚注制：没有独立的文献表，每一条引用都以脚注形式出现。下面是示例期刊论文按 {style} 引用后的效果：",
    labelJournalArticle: "期刊论文",
    labelBook: "图书",
    labelWebsite: "网页",
    labelFootnote: "脚注（期刊论文）",
    labelInText: "文内引用（针对该期刊论文）",
    aboutHeading: "关于 {style}",
    whenToUseLabel: "何时使用 {style}：",
    commonInLabel: "常用于：",
    subjectAreasLabel: "学科领域：",
    formattingRulesLabel: "格式规则：",
    parentStylePrefix: "{style} 遵循",
    parentStyleSuffix: "的格式——这是它所基于的母版样式。",
    howToHeading: "如何使用这个 {style} 生成器",
    howToBody:
      "将 DOI、新闻文章或网页的网址，或原始 BibTeX 粘贴到上方输入框。你也可以按书名或作者搜索图书，并通过 ISBN 引用。在样式下拉菜单中选择“{style}”（本页已预先选定），然后点击“生成引用”。将结果复制到你的文献表或参考文献列表中。复制前，你还可以编辑生成的条目（例如修正标题或补充日期）。",
    faqHeading: "关于 {style} 的常见问题",
    faqWhatIsQ: "什么是 {style}？",
    faqWhenQ: "什么时候应该使用 {style}？",
    faqInTextQ: "{style} 的文内引用是什么样的？",
    faqInTextA:
      "{style} 是{format}。引用示例期刊论文时，文内引用的写法如下：{example}",
    faqReferenceQ: "{style} 的文献表条目是什么样的？",
    faqReferenceA: "按 {style} 格式排版的期刊论文条目如下：{example}",
    faqWhereQ: "{style} 通常在哪些地方使用？",
    faqWhereA: "{style} 主要在{region}使用。{whenUsed}",
    faqRegionDefault: "全球范围内",
    faqGenerateQ: "如何生成 {style} 引用？",
    faqGenerateA:
      "使用本页的免费生成器：粘贴 DOI、网址（例如新闻报道链接）或 BibTeX，也可以按书名或作者搜索图书。选择 {style} 作为引用样式，点击“生成”即可得到排版好的参考文献，可直接复制到论文或文献列表中。",
    footerSupports:
      "支持来自世界各地的众多引用样式。我们的生成器使用的 Citation Style Language（CSL）样式来自",
    footerRepoLink: "官方 CSL 仓库",
  },

  // The source × style pages at `/<locale>/how-to-cite/<slug>` — 1,290 combinations.
  //
  // Same division as above: the citation templates, the worked examples generated from
  // each style's own CSL definition, the in-text samples and the hand-checked
  // formatting rules stay in the page module and in
  // `src/data/how-to-cite-examples-generated.json`. They are the citations themselves,
  // not prose about them. Source names, style names and editions come from
  // `src/data/how-to-cite.ts` and arrive here as placeholder values.
  crossLinks: {
    generateHeading: "生成此引用",
    generateBody:
      "粘贴 URL、DOI、ISBN 或来源信息，即可获得{source}的完整{style}引用。",
    generateCta: "打开{tool} →",
    guideHeading: "深入了解",
    guideCta: "阅读完整指南：{guide}",
    howToCiteHeading: "如何在各种格式中引用{source}",
    howToCiteBody: "最常用格式的规则、模板和完整示例。",
    howToCiteMore: "所有来源类型和格式 →",
    citeNowHeading: "立即用{style}格式引用{source}",
    citeNowHowTo: "格式规则与完整示例",
    citeNowTool: "用{tool}生成",
    citeNowHub: "{style}引用生成器",
    hubSourcesHeading: "如何用{style}格式引用常见来源",
    hubGuidesHeading: "{style}指南",
    hubCompareHeading: "{style}与其他格式的比较",
    moreInCollection: "{collection}中的更多工具",
    seeAllInCollection: "查看{collection}中的全部工具",
  },

  howToCitePage: {
    notFoundTitle: "未找到该页面",
    title: "如何用 {style}（{edition}）引用{source}",
    metaTitle: "如何用 {style} 引用{source}[{edition}，2026 年更新]",
    metaDescription:
      "了解如何用 {style} 格式（{edition}）引用{description}。提供免费示例、模板和文内引用指南。",
    keywordCite: "{source} {style} 引用",
    keywordCitation: "{style} {source} 引用格式",
    keywordHowTo: "如何引用{source}",
    keywordExample: "{style} 引用示例",
    keywordReference: "{source} {style} 参考文献",
    // What each style calls the list at the back of the paper, in the two casings the
    // prose needs. MLA says "Works Cited", Chicago and the legal styles "Bibliography".
    refListWorksCited: "引用文献",
    refListWorksCitedPage: "引用文献页",
    refListBibliography: "参考书目",
    refListBibliographyLower: "参考书目",
    refListReferenceList: "参考文献列表",
    refListReferenceListLower: "参考文献列表",
    backLink: "← 全部引用指南",
    lead: "了解如何用 {style} 格式（{edition}）引用{description}。本指南包含文献表格式、文内引用示例和关键格式规则。",
    referenceFormatHeading: "{style} {list}格式",
    referenceFormatIntro:
      "以下是在你的{list}中用 {style} 引用{source}的标准格式：",
    noTemplateNote: "此文献类型请按照 {style} 的标准模板处理。",
    workedHeading: "{source}的 {style} 示例",
    workedIntro:
      "{source}的完整 {style} 参考文献条目，由官方 {style} CSL 样式定义排版生成——与本站生成器使用的是同一引擎：",
    workedNoteOnlyIntro:
      "{style} 不会为{source}在{list}中单独列出条目——它只以注释形式引用。以下是该注释，由官方 {style} CSL 样式定义排版生成：",
    workedOscolaNote:
      "本站生成的 OSCOLA 输出遵循第 4 版的 CSL 样式，因为 Citation Style Language 仓库尚未发布第 5 版的 CSL 样式。请手动调整：把每个 ibid 换成简称加 (n X)，使用第 5 版的法院标识，把法规简称放入单引号，并优先使用永久链接或 DOI 而不是访问日期",
    workedCheckedIntro:
      "{source}的完整 {style} 参考文献条目，由人工撰写，并已对照 {style} 针对此类文献的官方指南进行核对：",
    workedCheckedNoteOnlyIntro:
      "{style} 不会为{source}在{list}中单独列出条目——它只以注释形式引用。以下是该注释，由人工撰写，并已对照 {style} 官方指南进行核对：",
    workedInTextLabel: "对应的文内引用：",
    inTextHeading: "{style} 文内引用",
    inTextIntro: "在论文正文中引用该文献时，可使用以下任意一种格式：",
    inTextParenthetical: "括号引用",
    inTextNarrative: "叙述引用",
    rulesHeading: "{style} 的关键格式规则",
    stepsHeading: "分步指南",
    step1Label: "收集文献信息：",
    step1Body: "收集{source}的作者姓名、标题、出版日期，以及网址或 DOI。",
    step2Label: "排版参考文献：",
    step2Body: "按照上文所示的 {style}（{edition}）模板排列各要素。",
    step3Label: "创建文内引用：",
    step3Body: "在论文中引用该文献之处，添加括号引用或叙述引用。",
    step4Label: "加入你的{list}：",
    step4Body: "在论文末尾列出完整的排版引用。",
    step5Label: "仔细核对格式：",
    step5Body: "检查斜体、标点、大小写和悬挂缩进是否符合 {style} 的要求。",
    ctaHeading: "自动生成这条引用",
    ctaBody:
      "跳过手动排版。将 DOI、网址或 ISBN 粘贴到 CitationEasy，几秒钟内即可得到完美排版的 {style} 引用。",
    ctaButton: "立即用 {style} 生成引用 →",
    otherStylesHeading: "用其他样式引用{source}",
    comboLink: "{style} 引用{source}",
    otherSourcesHeading: "用 {style} 引用其他文献",
    resourcesHeading: "更多资源",
    resourceGenerator: "免费参考文献生成器（{count} 种样式）",
    resourceTools: "免费写作工具",
    resourceCompare: "比较引用样式",
    resourceGuides: "全部引用指南",
    resourceCheatSheets: "速查表（APA、MLA、Chicago）",
    resourceGlossary: "引用术语表（et al.、ibid.、DOI、悬挂缩进）",
    // HowTo structured data. Deliberately worded differently from the visible steps
    // above: the schema describes the task generically, the page walks the reader
    // through this particular source and style.
    schemaStep1Name: "收集文献信息",
    schemaStep1Text:
      "收集引用{source}所需的全部信息，包括作者、标题、日期，以及网址或 DOI。",
    schemaStep2Name: "按 {style} 格式排版",
    schemaStep2Text:
      "按照 {style}（{edition}）的格式规则，将引用要素排列成正确顺序。",
    schemaStep3Name: "创建文内引用",
    schemaStep3Text: "在论文中使用 {style} 格式添加对应的文内引用。",
    schemaStep4Name: "加入参考文献列表",
    schemaStep4Text: "在论文末尾的{list}中列出完整引用。",
    faqHowQ: "如何用 {style} 引用{source}？",
    faqHowA:
      "要用 {style}（{edition}）引用{source}，需要包含作者、标题、日期和文献出处。使用我们的免费生成器即可自动完成排版。",
    faqFormatQ: "{source}的 {style} 引用格式是什么？",
    faqFormatA: "{source}的 {style} 格式为：{example}",
    faqFormatFallback:
      "{style} 格式要求按特定顺序排列作者、标题、日期和文献出处等具体要素。",
    faqInTextQ: "{source}在 {style} 中的文内引用是什么样的？",
    faqInTextA: "在 {style} 中，上例的文内引用为 {example}",
  },

  // The 43 source types the how-to-cite pages are built from (`SOURCE_TYPES` in
  // `src/data/how-to-cite.ts`), in the five forms those pages print. Each form is a whole
  // phrase, never an article and a noun joined in code: an article agrees with its noun
  // in gender, number and case, elides in French and Italian, and has no counterpart
  // at all in Polish, Russian or Japanese.
  //
  // - `…Name` ("Book") is the type on its own, as a label: `howToCitePage.comboLink`.
  //   English also lower-cases it into the `keyword*` templates.
  // - `…Title` ("a Book") is the type as the object of "cite" in a title or heading:
  //   `title`, `metaTitle`, `workedHeading` and `otherStylesHeading`, plus
  //   `crossLinks.generateBody`, `howToCiteHeading` and `citeNowHeading`. Through
  //   `title` it is also the HowTo name, the OG card and every link to the page.
  // - `…Phrase` ("a book") is the same inside a sentence: `referenceFormatIntro`,
  //   `workedIntro`, `workedNoteOnlyIntro` and the five `faq*` questions and answers.
  // - `…Noun` ("book") is the bare noun inside a sentence, where English puts "your" in
  //   front of it: `step1Body` and `schemaStep1Text`. A translation also uses it in the
  //   `keyword*` templates, which read as search queries.
  // - `…Description` is what the type covers: `{description}` in `lead` and
  //   `metaDescription`.
  //
  // A language with case picks one case per form and words every template above around
  // it — German writes Title and Phrase in the accusative, Polish and Russian write Name
  // and Noun in the nominative. Changing a form's case means re-reading every template
  // that receives it. A key missing here falls back to the English data
  // (`howToCiteSourceForms`), so a new source type renders in English, not as a key.
  // Chinese: no articles or case, so all four forms are the bare noun, worded as in `sourceTypes`.
  howToCiteSources: {
    websiteName: "网站",
    websiteTitle: "网站",
    websitePhrase: "网站",
    websiteNoun: "网站",
    websiteDescription: "网页、在线文章和其他网络内容",

    bookName: "图书",
    bookTitle: "图书",
    bookPhrase: "图书",
    bookNoun: "图书",
    bookDescription: "纸质图书和电子图书",

    journalArticleName: "期刊论文",
    journalArticleTitle: "期刊论文",
    journalArticlePhrase: "期刊论文",
    journalArticleNoun: "期刊论文",
    journalArticleDescription: "同行评审期刊和学术期刊上的论文",

    youtubeVideoName: "YouTube 视频",
    youtubeVideoTitle: "YouTube 视频",
    youtubeVideoPhrase: "YouTube 视频",
    youtubeVideoNoun: "YouTube 视频",
    youtubeVideoDescription: "各类在线视频，包括 YouTube 视频",

    podcastName: "播客",
    podcastTitle: "播客",
    podcastPhrase: "播客",
    podcastNoun: "播客",
    podcastDescription: "播客单集和整档播客节目",

    newspaperArticleName: "报纸文章",
    newspaperArticleTitle: "报纸文章",
    newspaperArticlePhrase: "报纸文章",
    newspaperArticleNoun: "报纸文章",
    newspaperArticleDescription: "印刷版和网络版报纸文章",

    magazineArticleName: "杂志文章",
    magazineArticleTitle: "杂志文章",
    magazineArticlePhrase: "杂志文章",
    magazineArticleNoun: "杂志文章",
    magazineArticleDescription: "印刷版和网络版杂志文章",

    blogPostName: "博客文章",
    blogPostTitle: "博客文章",
    blogPostPhrase: "博客文章",
    blogPostNoun: "博客文章",
    blogPostDescription: "博客文章和网络评论文章",

    wikipediaName: "维基百科",
    wikipediaTitle: "维基百科",
    wikipediaPhrase: "维基百科",
    wikipediaNoun: "维基百科",
    wikipediaDescription: "维基百科条目和其他维基词条",

    pdfName: "PDF 文件",
    pdfTitle: "PDF 文件",
    pdfPhrase: "PDF 文件",
    pdfNoun: "PDF 文件",
    pdfDescription: "以 PDF 格式发布的文档和报告",

    interviewName: "访谈",
    interviewTitle: "访谈",
    interviewPhrase: "访谈",
    interviewNoun: "访谈",
    interviewDescription: "私人访谈、已发表的访谈和广播电视访谈",

    socialMediaName: "社交媒体帖子",
    socialMediaTitle: "社交媒体帖子",
    socialMediaPhrase: "社交媒体帖子",
    socialMediaNoun: "社交媒体帖子",
    socialMediaDescription: "推文、Instagram 帖子、Facebook 帖子和 TikTok 视频",

    governmentDocumentName: "政府文件",
    governmentDocumentTitle: "政府文件",
    governmentDocumentPhrase: "政府文件",
    governmentDocumentNoun: "政府文件",
    governmentDocumentDescription: "政府报告、法律法规和官方文件",

    dissertationName: "学位论文",
    dissertationTitle: "学位论文",
    dissertationPhrase: "学位论文",
    dissertationNoun: "学位论文",
    dissertationDescription: "博士学位论文和硕士学位论文",

    conferencePaperName: "会议论文",
    conferencePaperTitle: "会议论文",
    conferencePaperPhrase: "会议论文",
    conferencePaperNoun: "会议论文",
    conferencePaperDescription: "会议论文集和会议报告",

    lectureNotesName: "讲座或 PowerPoint 幻灯片",
    lectureNotesTitle: "讲座或 PowerPoint 幻灯片",
    lectureNotesPhrase: "讲座或 PowerPoint 幻灯片",
    lectureNotesNoun: "讲座或 PowerPoint 幻灯片",
    lectureNotesDescription: "课堂讲座、幻灯片和课程资料",

    webinarName: "网络研讨会",
    webinarTitle: "网络研讨会",
    webinarPhrase: "网络研讨会",
    webinarNoun: "网络研讨会",
    webinarDescription: "直播或录播的网络研讨会",

    onlineCourseName: "在线课程",
    onlineCourseTitle: "在线课程",
    onlineCoursePhrase: "在线课程",
    onlineCourseNoun: "在线课程",
    onlineCourseDescription:
      "在线课程和 MOOC（Coursera、edX、Udemy、FutureLearn）",

    presentationSlidesName: "PowerPoint 演示文稿",
    presentationSlidesTitle: "PowerPoint 演示文稿",
    presentationSlidesPhrase: "PowerPoint 演示文稿",
    presentationSlidesNoun: "PowerPoint 演示文稿",
    presentationSlidesDescription: "各类演示幻灯片，包括 PowerPoint 演示文稿",

    imageName: "图片或照片",
    imageTitle: "图片或照片",
    imagePhrase: "图片或照片",
    imageNoun: "图片或照片",
    imageDescription: "照片、艺术作品、图表和网络图片",

    movieName: "电影",
    movieTitle: "电影",
    moviePhrase: "电影",
    movieNoun: "电影",
    movieDescription: "电影",

    documentaryName: "纪录片",
    documentaryTitle: "纪录片",
    documentaryPhrase: "纪录片",
    documentaryNoun: "纪录片",
    documentaryDescription: "纪录片和其他非虚构影片",

    tvShowName: "电视节目",
    tvShowTitle: "电视节目",
    tvShowPhrase: "电视节目",
    tvShowNoun: "电视节目",
    tvShowDescription: "电视节目或电视剧",

    songName: "歌曲或音乐",
    songTitle: "歌曲或音乐",
    songPhrase: "歌曲或音乐",
    songNoun: "歌曲或音乐",
    songDescription: "歌曲或音乐录音",

    poemName: "诗歌",
    poemTitle: "诗歌",
    poemPhrase: "诗歌",
    poemNoun: "诗歌",
    poemDescription: "诗歌、韵文以及选集或诗集中的诗作",

    courtCaseName: "法院判例",
    courtCaseTitle: "法院判例",
    courtCasePhrase: "法院判例",
    courtCaseNoun: "法院判例",
    courtCaseDescription: "法院判例或司法判决",

    patentName: "专利",
    patentTitle: "专利",
    patentPhrase: "专利",
    patentNoun: "专利",
    patentDescription: "专利或知识产权申请文件",

    encyclopediaName: "百科全书词条",
    encyclopediaTitle: "百科全书词条",
    encyclopediaPhrase: "百科全书词条",
    encyclopediaNoun: "百科全书词条",
    encyclopediaDescription: "百科全书中的文章或词条",

    dictionaryName: "词典词条",
    dictionaryTitle: "词典词条",
    dictionaryPhrase: "词典词条",
    dictionaryNoun: "词典词条",
    dictionaryDescription: "词典中的释义或词条",

    datasetName: "数据集",
    datasetTitle: "数据集",
    datasetPhrase: "数据集",
    datasetNoun: "数据集",
    datasetDescription: "数据集或数据库",

    softwareName: "软件或应用",
    softwareTitle: "软件或应用",
    softwarePhrase: "软件或应用",
    softwareNoun: "软件或应用",
    softwareDescription: "软件、应用或计算机程序",

    videoGameName: "电子游戏",
    videoGameTitle: "电子游戏",
    videoGamePhrase: "电子游戏",
    videoGameNoun: "电子游戏",
    videoGameDescription: "电子游戏和互动游戏软件",

    pressReleaseName: "新闻稿",
    pressReleaseTitle: "新闻稿",
    pressReleasePhrase: "新闻稿",
    pressReleaseNoun: "新闻稿",
    pressReleaseDescription: "新闻稿或新闻通稿",

    reportName: "报告",
    reportTitle: "报告",
    reportPhrase: "报告",
    reportNoun: "报告",
    reportDescription: "报告或白皮书",

    preprintName: "预印本",
    preprintTitle: "预印本",
    preprintPhrase: "预印本",
    preprintNoun: "预印本",
    preprintDescription: "发布在 arXiv、bioRxiv 等预印本平台上的论文",

    bookChapterName: "图书章节",
    bookChapterTitle: "图书章节",
    bookChapterPhrase: "图书章节",
    bookChapterNoun: "图书章节",
    bookChapterDescription: "编著图书中的章节和文章",

    chatgptName: "ChatGPT",
    chatgptTitle: "ChatGPT",
    chatgptPhrase: "ChatGPT",
    chatgptNoun: "ChatGPT",
    chatgptDescription: "包括 ChatGPT 在内的生成式 AI 工具",

    bibleName: "《圣经》",
    bibleTitle: "《圣经》",
    biblePhrase: "《圣经》",
    bibleNoun: "圣经",
    bibleDescription: "《圣经》、经文及其他宗教典籍",

    tedTalkName: "TED 演讲",
    tedTalkTitle: "TED 演讲",
    tedTalkPhrase: "TED 演讲",
    tedTalkNoun: "TED 演讲",
    tedTalkDescription: "包括 TED 演讲在内的会议演讲录像",

    emailName: "电子邮件",
    emailTitle: "电子邮件",
    emailPhrase: "电子邮件",
    emailNoun: "电子邮件",
    emailDescription: "电子邮件、信件和私人通信",

    redditName: "Reddit 或论坛帖子",
    redditTitle: "Reddit 或论坛帖子",
    redditPhrase: "Reddit 帖子",
    redditNoun: "Reddit 帖子",
    redditDescription: "网络论坛和讨论区帖子，包括 Reddit 讨论帖和 AMA 问答",

    tweetName: "X（Twitter）帖子",
    tweetTitle: "X（Twitter）帖子",
    tweetPhrase: "X（Twitter）帖子",
    tweetNoun: "X 帖子",
    tweetDescription: "推文和 X（原 Twitter）平台上的帖子",

    mapName: "地图",
    mapTitle: "地图",
    mapPhrase: "地图",
    mapNoun: "地图",
    mapDescription: "纸质地图、地图集以及 Google 地图等动态地图",

    claudeName: "Claude",
    claudeTitle: "Claude",
    claudePhrase: "Claude",
    claudeNoun: "Claude",
    claudeDescription: "Claude 对话及 Anthropic 的其他 AI 生成内容",

    geminiName: "Gemini",
    geminiTitle: "Gemini",
    geminiPhrase: "Gemini",
    geminiNoun: "Gemini",
    geminiDescription: "Google Gemini 对话和 AI 生成内容",

    copilotName: "Microsoft Copilot",
    copilotTitle: "Microsoft Copilot",
    copilotPhrase: "Microsoft Copilot",
    copilotNoun: "Microsoft Copilot",
    copilotDescription: "Microsoft Copilot 对话和 AI 回答",

    perplexityName: "Perplexity",
    perplexityTitle: "Perplexity",
    perplexityPhrase: "Perplexity",
    perplexityNoun: "Perplexity",
    perplexityDescription: "Perplexity AI 回答、对话串及其引用的来源",

    aiImageName: "AI 生成的图片",
    aiImageTitle: "AI 生成的图片",
    aiImagePhrase: "AI 生成的图片",
    aiImageNoun: "AI 生成的图片",
    aiImageDescription: "使用 DALL·E、Midjourney、Gemini 等 AI 工具生成的图片",

    tiktokName: "TikTok 视频",
    tiktokTitle: "TikTok 视频",
    tiktokPhrase: "TikTok 视频",
    tiktokNoun: "TikTok 视频",
    tiktokDescription: "TikTok 视频和创作者发布的内容",

    instagramName: "Instagram 帖子",
    instagramTitle: "Instagram 帖子",
    instagramPhrase: "Instagram 帖子",
    instagramNoun: "Instagram 帖子",
    instagramDescription: "Instagram 帖子、Reels 短视频和快拍",

    statuteName: "法律或法规",
    statuteTitle: "法律或法规",
    statutePhrase: "法律或法规",
    statuteNoun: "法律或法规",
    statuteDescription: "立法机关通过的法案、成文法和法典",

    isoStandardName: "ISO 标准",
    isoStandardTitle: "ISO 标准",
    isoStandardPhrase: "ISO 标准",
    isoStandardNoun: "ISO 标准",
    isoStandardDescription: "ISO、IEC 等已发布的技术标准",

    quranName: "《古兰经》",
    quranTitle: "《古兰经》",
    quranPhrase: "《古兰经》",
    quranNoun: "古兰经",
    quranDescription: "《古兰经》阿拉伯语原文或译本",

    ebookNoPageNumbersName: "无页码电子书",
    ebookNoPageNumbersTitle: "无页码电子书",
    ebookNoPageNumbersPhrase: "无页码电子书",
    ebookNoPageNumbersNoun: "无页码电子书",
    ebookNoPageNumbersDescription: "没有固定页码的 Kindle、EPUB 和在线电子书",

    speechName: "演讲",
    speechTitle: "演讲",
    speechPhrase: "演讲",
    speechNoun: "演讲",
    speechDescription: "现场或录制的演讲、致辞和布道",

    letterName: "信件",
    letterTitle: "信件",
    letterPhrase: "信件",
    letterNoun: "信件",
    letterDescription: "已出版的信件、档案馆藏信件和公开信",

    secondarySourceName: "二手文献（转引）",
    secondarySourceTitle: "二手文献（转引）",
    secondarySourcePhrase: "二手文献（转引）",
    secondarySourceNoun: "二手文献",
    secondarySourceDescription: "在其他文献中看到被引用或提及的来源",
  },

  // ---------------------------------------------------------------------------
  // NOT YET TRANSLATED. These five sections were copied verbatim from `en.ts` so
  // this file still satisfies `Dictionary`; the page types they cover
  // (`taxonomy`, `compare`, `convert`) are deliberately absent from
  // TRANSLATED_PAGE_TYPES, so nothing publishes them in this language yet. The
  // translation pass replaces the strings and only then adds the types.
  // ---------------------------------------------------------------------------
  // The three browse-by-facet pages under `/<locale>/citation-styles/…` — 32 academic
  // fields, 68 countries and 39 subject categories, 139 pages in one section because
  // they share a shell, a breadcrumb trail and a styles browser.
  //
  // The style NAMES, slugs and the CSL catalog itself stay in `src/data/**`: those are
  // subject matter. What the facets themselves are called is not — "Political Science",
  // "United Kingdom" and "Film & Media" are ordinary nouns that every reader expects in
  // their own language, and there are only 139 of them in total (see the `academicFields`,
  // `regions` and `styleCategories` sections below).
  taxonomyPage: {
    // ---- /citation-styles/field/<slug> ----
    fieldNotFoundTitle: "未找到",
    fieldMetaTitle: "{field}引用样式 - 2026年最佳参考文献格式",
    fieldMetaDescription:
      "了解{field}领域最常用的引用样式，为你的{fieldLower}论文、学位论文或期刊投稿找到合适的参考文献格式。",
    fieldOgTitle: "{field}的引用样式",
    fieldOgSubtitle: "常用样式：{styles}。",
    fieldOgKind: "按学科分类",
    fieldKeywordStyle: "{field}引用样式",
    fieldKeywordReferencing: "{field}参考文献格式",
    fieldKeywordBest: "{fieldLower}最佳引用样式",
    fieldKeywordBibliography: "{fieldLower}文献表格式",
    fieldKeywordDiscipline: "按学科分类的引用样式",
    fieldBackLink: "← 全部引用样式",
    fieldHeading: "{field}引用样式",
    fieldRecommendedHeading: "推荐引用样式",
    fieldRecommendedIntro:
      "以下引用样式常用于{field}领域的研究、期刊和课程作业。选择一种样式以了解详情并开始生成引用。",
    fieldStyleFallback: "一种用于{field}及相关学科的引用样式。",
    fieldRelatedHeading: "相关学科",
    fieldRelatedIntro: "探索相关学术领域中使用的引用样式：",
    fieldCtaHeading: "浏览全部引用样式",
    fieldCtaBody:
      "CitationEasy 支持覆盖各个学术领域的 {count} 种引用样式。浏览完整列表，找到你需要的确切格式。",
    fieldCtaButton: "浏览全部样式 →",
    fieldFaqWhichQ: "{field}领域使用哪种引用样式？",
    fieldFaqWhichA:
      "{field}领域使用的引用样式取决于具体期刊、机构或指导教师的要求。常见样式包括{styles}。请务必查看你的作业说明或目标期刊要求的格式。",
    fieldFaqSameQ: "所有{field}期刊都使用同一种样式吗？",
    fieldFaqSameA:
      "不是，{field}期刊并不都使用同一种引用样式。虽然某些样式在该领域更为常见，但各期刊可能有自己的格式要求。准备投稿前，请务必查看期刊的投稿指南。",
    fieldFaqChooseQ: "如何为我的{field}论文选择引用样式？",
    fieldFaqChooseA:
      "要为你的{field}论文选择引用样式，请先确认你的指导教师或目标期刊是否指定了必须使用的样式。如果没有，可使用上文列出的{field}常用推荐样式之一。CitationEasy 支持 {count} 余种样式，均可免费生成引用。",

    // ---- /citation-styles/country/<slug> ----
    countryNotFoundTitle: "未找到该国家/地区",
    countryMetaTitle: "{country}引用样式 - APA、MLA 及更多",
    countryMetaDescription:
      "浏览{country}常用的引用样式。免费生成{country}格式的参考文献。",
    countryOgTitle: "{country}常用的引用样式",
    countryOgKind: "按国家/地区分类",
    countryHeading: "{country}引用样式",
    countryIntroOne:
      "{count} 种常用于{country}或与其相关的引用样式。选择一种，进入专属生成器。",
    countryIntroMany:
      "{count} 种常用于{country}或与其相关的引用样式。选择一种，进入专属生成器。",
    countryBrowserTitle: "{country}引用样式",

    // ---- /citation-styles/type/<slug> ----
    typeNotFoundTitle: "未找到该类别",
    typeMetaTitle: "{type}引用样式 - 免费生成器",
    typeMetaDescription:
      "{typeLower}适用的引用样式，包括 APA、MLA、Vancouver 等。免费引用生成器。",
    typeOgTitle: "{type}引用样式",
    typeOgKind: "按主题分类",
    typeHeading: "{type}引用样式",
    typeIntroOne:
      "{count} 种适用于{typeLower}的引用样式。选择一种，进入带常见问题的专属生成器。",
    typeIntroMany:
      "{count} 种适用于{typeLower}的引用样式。选择一种，进入带常见问题的专属生成器。",
    typeBrowserTitle: "{type}引用样式",

    // ---- Shared chrome ----
    breadcrumbStyles: "引用样式",
    // Title case, because the BreadcrumbList markup says "Citation Styles" while the
    // visible trail says "Citation styles". Two keys rather than one so translating
    // either does not silently change the other.
    breadcrumbStylesSchema: "引用样式",
    breadcrumbByCountry: "按国家/地区分类",
    breadcrumbByDiscipline: "按学科分类",
    allStylesLink: "← 全部引用样式",
    internationalLabel: "国际通用",
  },

  // The 32 academic fields (`ACADEMIC_FIELDS` in src/data/style-pages.ts), keyed by
  // slug. Their one-sentence `description` is next door in `academicFieldDescriptions`.
  academicFields: {
    psychology: "心理学",
    education: "教育学",
    sociology: "社会学",
    anthropology: "人类学",
    politicalScience: "政治学",
    economics: "经济学",
    business: "商业与管理学",
    literature: "文学与语言学",
    history: "历史学",
    philosophy: "哲学",
    law: "法学与法律研究",
    medicine: "医学与临床科学",
    nursing: "护理学与助产学",
    publicHealth: "公共卫生与流行病学",
    pharmacy: "药学",
    biology: "生物学与生命科学",
    chemistry: "化学",
    physics: "物理学",
    engineering: "工程学",
    computerScience: "计算机科学",
    mathematics: "数学与统计学",
    environmentalScience: "环境科学",
    earthScience: "地球科学与地质学",
    artHistory: "艺术史与视觉艺术",
    music: "音乐与音乐学",
    theology: "神学与宗教研究",
    socialWork: "社会工作",
    linguistics: "语言学",
    architecture: "建筑学与城市规划",
    filmMedia: "电影与媒体研究",
    journalism: "新闻与传播学",
    criminology: "犯罪学与刑事司法",
  },

  // The same 32 fields' one-sentence ledes — the opening paragraph of every
  // `/citation-styles/field/<slug>` page.
  //
  // Unlike everything above, these are hand-written per field rather than mail-merged
  // from a template, which is why the extraction pass left them in
  // `src/data/style-pages.ts`: translating them is a content job, not a string swap.
  // But "hand-written" describes how they were produced, not where they belong. With
  // the chrome translated and the lede still English, the page is the hybrid CLAUDE.md
  // calls worse than an honest redirect — translated furniture around an English core.
  // `academicFieldDescription()` (src/lib/i18n/data-labels.ts) falls back to the data
  // file's own value, so a field added upstream is untranslated rather than blank.
  academicFieldDescriptions: {
    psychology: "常用于心理学、行为科学和认知研究的引用样式。",
    education: "适用于教育研究、教育学和课程研究的引用样式。",
    sociology: "用于社会学研究和社会理论的引用样式。",
    anthropology: "适用于人类学和民族志研究的引用样式。",
    politicalScience: "适用于政治学、政府研究和公共政策研究的引用样式。",
    economics: "用于经济学和计量经济学研究的引用样式。",
    business: "适用于商业、管理、市场营销和金融研究的引用样式。",
    literature: "适用于文学研究、语言学和语言研究的引用样式。",
    history: "适用于历史研究、史学史和档案学研究的引用样式。",
    philosophy: "适用于哲学著作和伦理学研究的引用样式。",
    law: "适用于法院文件、法学评论和法学著作的法律引用样式。",
    medicine: "适用于医学研究、临床研究和医疗保健领域的引用样式。",
    nursing: "适用于护理学研究和医疗实践的引用样式。",
    publicHealth: "适用于公共卫生研究和流行病学研究的引用样式。",
    pharmacy: "适用于药学和药理学研究的引用样式。",
    biology: "适用于生物学研究、生态学和遗传学的引用样式。",
    chemistry: "适用于化学、生物化学和材料科学研究的引用样式。",
    physics: "适用于物理学、天体物理学和应用物理学研究的引用样式。",
    engineering: "适用于工程学和应用技术研究的引用样式。",
    computerScience: "适用于计算机科学、人工智能和软件工程研究的引用样式。",
    mathematics: "适用于数学、应用数学和统计学研究的引用样式。",
    environmentalScience: "适用于环境研究和可持续发展研究的引用样式。",
    earthScience: "适用于地质学、地球物理学和地球科学研究的引用样式。",
    artHistory: "适用于艺术史、视觉文化和博物馆学研究的引用样式。",
    music: "适用于音乐研究、音乐理论和表演研究的引用样式。",
    theology: "适用于神学和宗教研究著作的引用样式。",
    socialWork: "适用于社会工作研究和实务的引用样式。",
    linguistics: "适用于语言学、语音学和语言科学研究的引用样式。",
    architecture: "适用于建筑学、城市设计和规划研究的引用样式。",
    filmMedia: "适用于电影、电视、媒体和传播学研究的引用样式。",
    journalism: "适用于新闻学、媒体研究和传播学研究的引用样式。",
    criminology: "适用于犯罪学和刑事司法研究的引用样式。",
  },

  // The 68 country/region facets (`REGIONS` in src/data/citation-styles.ts), keyed by
  // slug. `getRegionCountryCode` still keys off the English data value, so translating these
  // moves the label without moving the flag.
  regions: {
    unitedStates: "美国",
    unitedKingdom: "英国",
    australia: "澳大利亚",
    canada: "加拿大",
    newZealand: "新西兰",
    ireland: "爱尔兰",
    southAfrica: "南非",
    germany: "德国",
    france: "法国",
    spain: "西班牙",
    italy: "意大利",
    netherlands: "荷兰",
    belgium: "比利时",
    switzerland: "瑞士",
    austria: "奥地利",
    portugal: "葡萄牙",
    luxembourg: "卢森堡",
    sweden: "瑞典",
    norway: "挪威",
    denmark: "丹麦",
    finland: "芬兰",
    iceland: "冰岛",
    poland: "波兰",
    czechRepublic: "捷克",
    hungary: "匈牙利",
    romania: "罗马尼亚",
    russia: "俄罗斯",
    ukraine: "乌克兰",
    greece: "希腊",
    turkey: "土耳其",
    croatia: "克罗地亚",
    slovenia: "斯洛文尼亚",
    slovakia: "斯洛伐克",
    serbia: "塞尔维亚",
    bulgaria: "保加利亚",
    lithuania: "立陶宛",
    latvia: "拉脱维亚",
    estonia: "爱沙尼亚",
    japan: "日本",
    china: "中国",
    southKorea: "韩国",
    india: "印度",
    singapore: "新加坡",
    hongKong: "中国香港",
    taiwan: "中国台湾",
    malaysia: "马来西亚",
    indonesia: "印度尼西亚",
    thailand: "泰国",
    vietnam: "越南",
    philippines: "菲律宾",
    pakistan: "巴基斯坦",
    bangladesh: "孟加拉国",
    sriLanka: "斯里兰卡",
    israel: "以色列",
    saudiArabia: "沙特阿拉伯",
    unitedArabEmirates: "阿拉伯联合酋长国",
    brazil: "巴西",
    mexico: "墨西哥",
    argentina: "阿根廷",
    chile: "智利",
    colombia: "哥伦比亚",
    peru: "秘鲁",
    venezuela: "委内瑞拉",
    egypt: "埃及",
    nigeria: "尼日利亚",
    kenya: "肯尼亚",
    morocco: "摩洛哥",
    international: "国际通用",
  },

  // The 39 subject categories (`CATEGORIES` in src/data/citation-styles.ts), keyed by
  // slug. The emoji beside each one stays in the data — it is language-neutral.
  styleCategories: {
    medicine: "医学",
    nursing: "护理学",
    publicHealth: "公共卫生",
    pharmacy: "药学",
    dentistry: "牙科学",
    veterinary: "兽医学",
    psychology: "心理学",
    sociology: "社会学",
    anthropology: "人类学",
    politicalScience: "政治学",
    economics: "经济学",
    education: "教育学",
    socialWork: "社会工作",
    biology: "生物学",
    chemistry: "化学",
    physics: "物理学",
    environmentalScience: "环境科学",
    earthScience: "地球科学与地质学",
    agriculture: "农学",
    ecology: "生态学",
    engineering: "工程学",
    computerScience: "计算机科学",
    mathematics: "数学",
    statistics: "统计学",
    history: "历史学",
    literature: "文学",
    philosophy: "哲学",
    linguistics: "语言学",
    artHistory: "艺术史",
    music: "音乐",
    filmMedia: "电影与媒体",
    archaeology: "考古学",
    law: "法学",
    business: "商业",
    architecture: "建筑学",
    religion: "宗教与神学",
    geography: "地理学",
    general: "综合/跨学科",
    other: "其他",
  },

  // The closed vocabularies `STYLE_LANDING_PAGES` (src/data/style-pages.ts) draws on for
  // the 30 major styles: what each style calls its end-of-paper list, the shape of its
  // in-text citation, its edition, and the disciplines that require it. The comparison
  // pages print all four in a table and the field pages print two of them under every
  // style card, so leaving them in the data means a translated page with an English
  // table in the middle of it.
  //
  // Style NAMES (`name`, `fullName`) are NOT here: a style's official name is what it
  // is called everywhere. The 30 hand-written taglines are, in `styleTaglines` below.
  styleFacts: {
    // `referenceListName` — 6 distinct values across the 30 styles.
    listReferenceList: "参考文献列表",
    listWorksCited: "引用文献",
    listReferences: "参考文献",
    listBibliography: "参考书目",
    listTableOfAuthorities: "法律引证表",
    listReferencesCited: "引用参考文献",
    listLowerReferenceList: "参考文献列表",
    listLowerWorksCited: "引用文献",
    listLowerReferences: "参考文献",
    listLowerBibliography: "参考书目",
    listLowerTableOfAuthorities: "法律引证表",
    listLowerReferencesCited: "引用参考文献",

    // `inTextFormat` — 14 distinct values. "Author", "Year" and "Page" are slot names,
    // not part of the citation, so they read as English words on a Spanish page.
    inTextAuthorCommaYear: "（作者，年份）",
    inTextAuthorPage: "（作者 页码）",
    inTextAuthorYear: "（作者 年份）",
    inTextSuperscriptOrBracketed: "上标或括号数字 (1)",
    inTextBracketedNumbers: "方括号数字 [1]",
    inTextSuperscriptNumbers: "上标数字¹",
    inTextSuperscriptOrAuthorYear: "上标数字或（作者 年份）",
    inTextKeyBased: "基于键的引用",
    inTextSuperscriptOrAuthorCommaYear: "上标数字或（作者，年份）",
    inTextFootnotesCaseLegislation: "包含判例与法规引用的脚注",
    inTextFootnotesSignals: "带引证信号缩写的脚注",
    inTextFootnotesPinpoint: "带精确页码引用的脚注",
    inTextParenNumbers: "圆括号数字 (1)",
    inTextItalicNumbers: "斜体数字 (1)",

    // `edition` — 12 distinct values. The ordinal and the word "edition" translate; the
    // handbook name inside "Cite Them Right 11th edition" does not.
    edition4th: "第 4 版",
    edition5th: "第 5 版",
    edition7th: "第 7 版",
    edition8th: "第 8 版",
    edition9th: "第 9 版",
    edition11th: "第 11 版",
    edition18th: "第 18 版",
    edition22nd: "第 22 版",
    editionCiteThemRight11th: "Cite Them Right 第 11 版",
    editionIcmjeStandard: "ICMJE 标准",
    editionStandard: "标准",
    editionCurrent: "现行版",

    // `fields` — the 67 discipline names the 30 styles are tagged with.
    fieldAcoustics: "声学",
    fieldAnthropology: "人类学",
    fieldAppliedPhysics: "应用物理学",
    fieldArchaeology: "考古学",
    fieldArts: "艺术",
    fieldAstrophysics: "天体物理学",
    fieldAustralianLaw: "澳大利亚法律",
    fieldBiochemistry: "生物化学",
    fieldBiology: "生物学",
    fieldBiomedicalSciences: "生物医学",
    fieldBiotechnology: "生物技术",
    fieldBusiness: "商业",
    fieldCellBiology: "细胞生物学",
    fieldChemicalEngineering: "化学工程",
    fieldChemistry: "化学",
    fieldClinicalResearch: "临床研究",
    fieldComputerScience: "计算机科学",
    fieldCondensedMatter: "凝聚态物理学",
    fieldCourtDocuments: "法庭文书",
    fieldCriminology: "犯罪学",
    fieldCulturalStudies: "文化研究",
    fieldDemography: "人口学",
    fieldEcology: "生态学",
    fieldEducation: "教育学",
    fieldElectronics: "电子学",
    fieldEngineering: "工程学",
    fieldEnvironmentalScience: "环境科学",
    fieldEpidemiology: "流行病学",
    fieldEthnography: "民族志学",
    fieldGenetics: "遗传学",
    fieldGeology: "地质学",
    fieldGlobalHealth: "全球卫生",
    fieldHealthSciences: "健康科学",
    fieldHistory: "历史学",
    fieldHumanities: "人文学科",
    fieldInformationSystems: "信息系统",
    fieldInformationTechnology: "信息技术",
    fieldInternationalRelations: "国际关系",
    fieldJurisprudence: "法理学",
    fieldLanguages: "语言学科",
    fieldLaw: "法学",
    fieldLawReview: "法律评论",
    fieldLegalStudies: "法律研究",
    fieldLifeSciences: "生命科学",
    fieldLiterature: "文学",
    fieldMaterialsScience: "材料科学",
    fieldMathematics: "数学",
    fieldMedicine: "医学",
    fieldMolecularBiology: "分子生物学",
    fieldMultidisciplinary: "多学科",
    fieldMultidisciplinaryScience: "多学科科学",
    fieldNaturalSciences: "自然科学",
    fieldNursing: "护理学",
    fieldPharmacy: "药学",
    fieldPhysics: "物理学",
    fieldPoliticalScience: "政治学",
    fieldPsychology: "心理学",
    fieldPublicHealth: "公共卫生",
    fieldPublicPolicy: "公共政策",
    fieldPublishing: "出版业",
    fieldScience: "科学",
    fieldSciences: "科学学科",
    fieldSocialSciences: "社会科学",
    fieldSocialWork: "社会工作",
    fieldSociology: "社会学",
    fieldSoftwareEngineering: "软件工程",
    fieldTheology: "神学",
  },

  // The 30 major styles' hero taglines (`STYLE_LANDING_PAGES[].tagline`), keyed by
  // slug.
  //
  // Per-page hand-written copy, not a template — which is why they outlived the
  // extraction pass in `src/data/style-pages.ts`. Three page types print them: the
  // style landing page's hero, the card for each recommended style on a field
  // taxonomy page, and the lower-cased clause inside a comparison page's opening
  // sentence ("APA (…) is the most widely used citation style in the social
  // sciences"). Leaving them in the data means an English sentence in the first
  // paragraph of an otherwise translated page. `styleTagline()`
  // (src/lib/i18n/data-labels.ts) falls back to the data file's own value.
  styleTaglines: {
    apa: "社会科学领域使用最广泛的引用样式",
    mla: "人文学科的标准引用样式",
    chicago: "全美作者、编辑和出版商最信赖的样式指南",
    harvard: "英国和澳大利亚高校中最流行的参考文献样式",
    vancouver: "医学与生物医学期刊的标准引用样式",
    ieee: "工程学和计算机科学领域的首选引用样式",
    ama: "美国医学期刊的引用标准",
    turabian: "面向学生的简化版 Chicago 格式",
    cse: "自然科学与物理科学领域首选的引用样式",
    bibtex: "面向 LaTeX 用户和学术排版的文献目录格式",
    nature: "全球顶尖跨学科期刊采用的引用样式",
    acs: "化学与化学相关学科的引用标准",
    oscola: "英国法学院和法律期刊的标准法律引用样式",
    bluebook: "美国法院和法律评论权威采用的法律引用体系",
    aglc: "澳大利亚法学院和法院的标准法律引用样式",
    asa: "社会学与社会研究的标准引用样式",
    apsa: "政治学研究与期刊使用的引用样式",
    aaa: "人类学研究的标准引用样式",
    nlm: "PubMed 与美国国立医学图书馆采用的引用标准",
    acm: "ACM 会议与计算研究使用的引用样式",
    aps: "Physical Review 与 APS 系列期刊使用的引用样式",
    aip: "AIP 与物理学期刊使用的引用样式",
    cell: "Cell Press 及主要生物学期刊使用的引用样式",
    science: "Science 杂志与 AAAS 系列期刊使用的引用样式",
    plos: "PLOS 开放获取期刊使用的引用样式",
    bmj: "BMJ 及英国主要医学期刊使用的引用样式",
    lancet: "The Lancet 系列医学期刊使用的引用样式",
    jama: "JAMA 及 JAMA Network 系列期刊使用的引用样式",
    elsevierHarvard: "Elsevier 旗下期刊通用的 Harvard 样式变体",
    springer: "Springer Nature 期刊与图书使用的引用样式",
  },

  // The 105 style-versus-style pages at `/<locale>/compare/<slug>`.
  //
  // The two style names arrive as placeholder values and are never translated. The
  // facts in the table come from `styleFacts` above and the taglines from
  // `styleTaglines`; only `fullName` stays in `src/data/style-pages.ts`, because an
  // official name is the same in every language.
  comparePage: {
    notFoundTitle: "未找到",
    metaTitle: "{a} 对比 {b} - 主要差异详解（2026年更新）",
    metaDescription:
      "并排比较 {a} 和 {b} 引用样式，了解排版、文内引用和参考文献列表方面的主要差异，为你的论文选择合适的样式。",
    ogTitle: "{a} 对比 {b}",
    ogSubtitle:
      "文内格式、参考文献条目、大小写规则，以及各学科分别要求使用哪种样式。",
    ogKind: "样式对比",
    keywordVs: "{a} 对比 {b}",
    keywordVersus: "{a} 与 {b} 对比",
    keywordDifference: "{a} 和 {b} 的区别",
    keywordOr: "{a} 还是 {b}",
    keywordComparison: "引用样式对比",
    breadcrumbCompare: "样式对比",
    breadcrumbCurrent: "{a} 对比 {b}",
    backLink: "← 返回对比工具",
    heading: "{a} 对比 {b}：主要差异",
    intro:
      "{a} 和 {b} 是学术界使用最广泛的两种引用样式，但两者在一些重要方面存在差异。{introA} {introB} 了解这些差异，能帮助你为论文选择合适的格式。",
    styleIntro: "{style}（{fullName}）{tagline}。",
    styleIntroFallback: "{style} 是一种在许多学术领域广泛使用的常见引用样式。",
    tableHeading: "并排对比",
    tableFeature: "特性",
    tableCitationSystem: "引用体系",
    tableInText: "文内格式",
    tableReferenceList: "文献表名称",
    tableEdition: "版本",
    tableCommonFields: "常用领域",
    // Title case, for the table cell. `styleLanding.system*` says "Author–date" for the
    // fact list on the style landing pages; both spellings are deliberate.
    systemAuthorDate: "著者—出版年制",
    systemNumbered: "顺序编码制",
    systemFootnotes: "脚注制",
    // The same three systems inside a sentence ("uses a numbered citation system").
    describeAuthorDate: "夹注式著者—出版年制",
    describeNumbered: "顺序编码制",
    describeFootnotes: "脚注制",
    whenToUseHeading: "何时使用 {style}",
    whenToUseBody:
      "{style}（{fullName}）采用{system}引用体系，文内引用为{inText}，论文末尾附有{list}。最常用于{fields}{more}。",
    whenToUseMoreFields: "及相关领域",
    whenToUseFallback:
      "{style} 是一种广泛认可的引用样式。请查看你的作业说明或目标期刊要求，确认是否需要使用 {style} 格式。",
    ctaHeading: "查看实际差异",
    ctaBodyBefore: "试试我们的",
    ctaBodyLink: "交互式对比工具",
    ctaBodyAfter: "，用你自己的文献并排比较 {a} 和 {b} 引用。",
    ctaCiteIn: "以 {style} 引用 →",
    faqDifferenceQ: "{a} 和 {b} 有什么区别？",
    faqDifferenceA:
      "{a} 和 {b} 在文内引用格式、参考文献列表结构以及使用它们的学术领域上有所不同。{detail}",
    faqDifferenceDetail:
      "{a} 使用{inTextA}文内引用，并附有{listA}；而 {b} 使用{inTextB}，并附有{listB}。",
    faqDifferenceDetailFallback:
      "每种样式在引用格式、文献表编排和不同文献类型的处理上都有各自独特的规则。",
    faqBetterQ: "{a} 和 {b} 哪个更好？",
    faqBetterA:
      "{a} 和 {b} 并无绝对的优劣之分。正确的选择取决于你的学术领域、指导教师的要求，或投稿期刊的规定。请查看你的作业说明或目标出版物，确定应使用哪种样式。",
    faqSwitchQ: "我可以在 {a} 和 {b} 之间切换吗？",
    faqSwitchA:
      "可以，你可以使用 CitationEasy 的免费引用生成器在 {a} 和 {b} 之间切换。只需选择所需样式，你的引用就会自动重新排版。不过，除非指导教师另有说明，否则一篇论文通常只应使用一种样式。",
  },

  // The ~21 converter-pair pages at `/<locale>/convert/<slug>`.
  //
  // Only the shell is here; each pair's own four strings are in `converterCopy` below.
  // The format names (DOI, ISBN, BibTeX, RIS, CSL-JSON, PubMed ID, arXiv ID, Word) are
  // identifiers, never translated.
  converterPage: {
    notFoundTitle: "未找到该转换工具",
    metaTitle: "{title} - 免费即时转换",
    ogTitle: "{title}",
    ogKind: "转换工具",
    breadcrumbConverters: "转换工具",
    breadcrumbLabel: "面包屑导航",
    whenHeading: "适用场景",
    relatedHeading: "相关转换工具",
    allConverters: "全部转换工具",
    formatConverterTool: "引用格式转换器",
    faqHowQ: "如何将{input}转换为{output}？",
    faqHowA: "{intro}全部功能免费使用，无需注册，也没有转换次数限制。",
    faqPrivacyQ: "我的数据会被上传到任何地方吗？",
    faqPrivacyIdentifier:
      "每个{input}都会在相应的公共元数据服务中查询（DOI 对应 Crossref，PMID 对应 PubMed，ISBN 对应图书馆记录），仅发送标识符本身——绝不会发送你的文档。生成的结果可供你复制或下载；不会被存储。",
    faqPrivacyLocal:
      "不会——你粘贴或上传的{input}完全在你的浏览器中解析。{detail}",
    faqPrivacyDetailStyle:
      "只有解析出的参考文献字段（作者、标题、年份）会被发送到我们的格式化引擎以生成引用；你的文件本身绝不会离开你的设备。",
    faqPrivacyDetailOffline:
      "转换过程完全在页面内离线完成——不会向任何服务器发送任何内容。",
    faqUsefulQ: "{input}转{output}的转换在什么情况下有用？",
    faqOtherQ: "我还可以转换成其他样式和格式吗？",
    faqOtherA:
      "可以。CitationEasy 的生成器可以将任意文献格式化为 {count} 种引用样式，参考文献列表可导出为 Word、RTF、BibTeX、RIS 和 CSL-JSON，转换中心还列出了所有直接转换对。如果你需要将已排版好的文本从一种样式改写为另一种样式，请改用引用格式转换器工具。",
  },

  // The 21 converter pairs' own copy (`CONVERTER_PAIRS` in src/data/converters.ts),
  // keyed by slug: title, meta description, the intro paragraph under the H1, and the
  // "when you need this" paragraph.
  //
  // Four hand-written strings per pair, and `src/data/converters.ts` has a quality rule
  // saying they must stay hand-written — no mail-merged sentences, because mass-templated
  // pages are what earned the site a scaled-content penalty. That rule is about how the
  // copy is authored; it says nothing about which language a reader gets it in. Left in
  // the data file they are the entire body of an otherwise translated page.
  // `converterPairCopy()` (src/lib/i18n/data-labels.ts) returns all four at once and
  // falls back to the data file per field, so a converter added upstream ships English
  // copy rather than a blank page.
  converterCopy: {
    doiToApaTitle: "DOI 转 APA 引用转换器",
    doiToApaMetaDescription:
      "逐行粘贴 DOI，即可从出版商官方 Crossref 元数据中即时生成 APA 第 7 版参考文献。免费，无需注册。",
    doiToApaIntro:
      "DOI 本身就指向出版商的权威元数据，因此完全没必要手动输入 APA 参考文献。每行粘贴一个 DOI，即可得到完整的 APA 第 7 版参考文献——作者姓名倒置、句首大写的标题、斜体期刊名，以及 APA 要求的 https://doi.org/ 链接一应俱全。",
    doiToApaUseCase:
      "典型场景：导师要求提交 APA 参考文献列表，而你手头只有阅读时保存的一堆 DOI。粘贴进去，复制列表即可。",
    doiToMlaTitle: "DOI 转 MLA 引用转换器",
    doiToMlaMetaDescription:
      "利用 Crossref 元数据，自动将 DOI 转换为 MLA 第 9 版引用文献条目。逐行粘贴 DOI 即可——免费，无需安装任何软件。",
    doiToMlaIntro:
      "MLA 第 9 版把期刊论文当作一条“容器链”处理——加引号的文章标题、斜体期刊名、卷号、期号、年份、页码，手动排列相当繁琐。DOI 查询会直接从出版商记录中填好每一项，引用文献条目一次就能排对。",
    doiToMlaUseCase:
      "适用于人文课程要求 MLA、而资料却来自只给 DOI 的理工类数据库的情况。",
    doiToBibtexTitle: "DOI 转 BibTeX 转换器",
    doiToBibtexMetaDescription:
      "将 DOI 转换为适用于 LaTeX 和 Overleaf 的 BibTeX 条目。逐行粘贴 DOI，下载现成的 .bib 文件——免费且不限次数。",
    doiToBibtexIntro:
      "每一篇 LaTeX 论文最终都需要一个 .bib 文件。逐行粘贴 DOI，即可得到带有自动生成引用键的整洁 @article 条目，可直接用于 Overleaf、natbib 或 biblatex——无需从出版商页面手动复制字段。",
    doiToBibtexUseCase:
      "经典场景：审稿人发来三篇需要引用的论文，你手头只有它们的 DOI，而截止日期就是今晚。粘贴、下载，然后直接 \\cite。",
    doiToRisTitle: "DOI 转 RIS 转换器",
    doiToRisMetaDescription:
      "将 DOI 转换为 Zotero、Mendeley 和 EndNote 可直接导入的 RIS 文件。逐行粘贴 DOI 即可——免费，无需注册。",
    doiToRisIntro:
      "RIS 是所有文献管理软件都能导入的交换格式。粘贴 DOI 并下载 .ris 文件，其中 TY/AU/TI/T2 字段直接来自 Crossref——之后在 Zotero、Mendeley 或 EndNote 中选择“文件→导入”，条目就会以完整结构导入。",
    doiToRisUseCase:
      "非常适合把手动收集的 DOI 列表迁移到正规的文献管理软件中，一个作者姓名都不用重新输入。",
    isbnToApaTitle: "ISBN 转 APA 引用转换器",
    isbnToApaMetaDescription:
      "自动将 ISBN 转换为 APA 第 7 版图书参考文献。逐行粘贴 ISBN，复制整理好的列表——免费，无需注册。",
    isbnToApaIntro:
      "书籍版权页上的 ISBN 就是生成 APA 参考文献所需的全部信息。每个 ISBN 都会与图书馆元数据进行比对，并按 APA 第 7 版格式整理——作者、年份、斜体书名和出版社一应俱全，记录中若含版次信息也会一并显示。",
    isbnToApaUseCase:
      "引用一摞实体图书馆藏书的最快方法：翻开每本书的版权页，输入 ISBN，粘贴到这里即可生成列表。",
    isbnToMlaTitle: "ISBN 转 MLA 引用转换器",
    isbnToMlaMetaDescription:
      "一次粘贴即可将 ISBN 转换为 MLA 第 9 版引用文献条目。免费 ISBN 查询，直接输出整理好的格式——无需注册。",
    isbnToMlaIntro:
      "MLA 第 9 版的图书条目很简短——作者、斜体书名、出版社、年份——但作者姓名倒置和标点符号仍然容易出错。ISBN 查询会调取图书的真实目录记录，并按 MLA 手册的核心要素模板进行排版。",
    isbnToMlaUseCase:
      "非常适合文学论文：只需输入书籍封底的数字，即可引用桌上的每一部小说和评论专著。",
    isbnToBibtexTitle: "ISBN 转 BibTeX 转换器",
    isbnToBibtexMetaDescription:
      "将 ISBN 转换为适用于 LaTeX 的 BibTeX @book 条目。逐行粘贴 ISBN，下载 .bib 文件——免费且即时完成。",
    isbnToBibtexIntro:
      "图书是 BibTeX 中比较棘手的一类——没有 DOI 可查，出版商页面也很少提供 .bib 导出。这个转换器会将每个 ISBN 与图书馆元数据比对，生成填好作者、书名、出版社和年份字段的 @book 条目。",
    isbnToBibtexUseCase:
      "适合论文写作者：方法章节引用的是期刊 DOI，而理论章节要引用十几本书，这里正好可以处理这些书。",
    pmidToApaTitle: "PMID 转 APA 引用转换器",
    pmidToApaMetaDescription:
      "自动将 PubMed ID 转换为 APA 第 7 版参考文献。逐行粘贴 PMID，即可获得整理好的参考文献列表——免费。",
    pmidToApaIntro:
      "PubMed 上的每条记录都有一个 PMID，而心理学和护理学专业绝大多数都要求使用 APA。逐行粘贴 PMID，每一条都会通过 PubMed 自身的元数据解析为完整的 APA 第 7 版期刊参考文献，记录中若含 DOI 也会一并附上链接。",
    pmidToApaUseCase:
      "专为循证实践类作业设计：检索策略最终得到一份 PMID 列表，而论文需要将它们整理成 APA 格式。",
    pmidToRisTitle: "PMID 转 RIS 转换器",
    pmidToRisMetaDescription:
      "将 PubMed ID 转换为适用于 Zotero、Mendeley 或 EndNote 的 RIS 文件。逐行粘贴 PMID，下载 .ris 文件——免费。",
    pmidToRisIntro:
      "系统综述的工作流程离不开 RIS 文件。将 PubMed 检索结果中的 PMID 粘贴进来，即可下载可直接用于筛选工具或文献管理软件的 .ris 文件——每条记录都基于 PubMed 的规范元数据结构化，而非从网页抓取的文本。",
    pmidToRisUseCase:
      "适合综述进行到一半时：同事发来邮件说“再加上这五项研究”，只给了 PMID，而你的文献库正好在 EndNote 中。",
    arxivToBibtexTitle: "arXiv 转 BibTeX 转换器",
    arxivToBibtexMetaDescription:
      "将 arXiv 编号转换为适用于 LaTeX 论文的 BibTeX 条目。逐行粘贴如 2301.12345 这样的编号，下载 .bib 文件——免费。",
    arxivToBibtexIntro:
      "在快速发展的领域中，预印本被频繁引用，而 arXiv 编号（如 2301.12345）正是它们流通的方式。粘贴编号即可获得包含预印本作者、标题、年份和 arXiv 链接的 BibTeX 条目，可直接用于论文的 .bib 文件。",
    arxivToBibtexUseCase:
      "适合相关工作部分八成都是 arXiv 文献的机器学习和物理学作者：阅读时随手收集编号，写作时一次性粘贴转换。",
    bibtexToApaTitle: "BibTeX 转 APA 转换器",
    bibtexToApaMetaDescription:
      "粘贴或上传 .bib 文件，即可获得 APA 第 7 版参考文献列表。支持包含多条目的文件——免费，解析在浏览器中完成。",
    bibtexToApaIntro:
      "有时论文会离开 LaTeX 环境——期刊要求提交 Word 稿件，或委员会要求以纯文本形式提供参考文献列表。粘贴你的 BibTeX（或上传 .bib 文件），每个条目都会在浏览器中解析，并整理为可直接放入任意文档的 APA 第 7 版参考文献列表。",
    bibtexToApaUseCase:
      "适用于基金申请这类场景：你的发表列表保存在 .bib 文件中，而资助方的表格要求在文本框中填写 APA 格式的参考文献。",
    bibtexToMlaTitle: "BibTeX 转 MLA 转换器",
    bibtexToMlaMetaDescription:
      "将 BibTeX 转换为 MLA 第 9 版引用文献条目。粘贴或上传 .bib 文件，复制整理好的列表——免费，无需注册。",
    bibtexToMlaIntro:
      "BibTeX 只存储原始字段；而 MLA 则精确规定了这些字段的排列方式。这个转换器会在本地解析你的 .bib 条目，并按照 MLA 第 9 版模板逐一排版——包括作者姓名倒置、加引号的文章标题、斜体容器名称，以及各要素之间正确的标点。",
    bibtexToMlaUseCase:
      "适合跨领域学者：你的参考文献保存在过去 LaTeX 工作留下的 BibTeX 中，而你要投稿的人文学科期刊却要求使用 MLA。",
    bibtexToRisTitle: "BibTeX 转 RIS 转换器",
    bibtexToRisMetaDescription:
      "将 .bib 文件转换为适用于 Zotero、Mendeley 或 EndNote 的 RIS。粘贴或上传 BibTeX，下载 .ris 文件——免费且离线完成。",
    bibtexToRisIntro:
      "从 LaTeX 工具迁移到文献管理软件，意味着要把 BibTeX 的 @article/@book 条目转换为 RIS 的 TY/AU/TI 标签。整个转换过程——包括字段映射——都在浏览器中完成，并生成一个任何管理软件都能导入的 .ris 文件。",
    bibtexToRisUseCase:
      "典型触发场景：新加入的合著者习惯用 Zotero，而共享的参考文献是代码库里的一个 .bib 文件，总得有人来弥合这个差异。",
    bibtexToCslJsonTitle: "BibTeX 转 CSL-JSON 转换器",
    bibtexToCslJsonMetaDescription:
      "将 BibTeX 转换为适用于 Pandoc 和 Quarto 的 CSL-JSON。粘贴或上传 .bib 文件，下载 references.json——免费，浏览器内完成。",
    bibtexToCslJsonIntro:
      "Pandoc 和 Quarto 原生支持从 CSL-JSON 引用，转换现有的 .bib 文件可以从构建流程中去掉一个环节。条目会在浏览器中解析，输出为干净的 CSL-JSON 数组——正是 --bibliography=references.json 所需要的格式。",
    bibtexToCslJsonUseCase:
      "适合以 Markdown 为主、正在把旧 LaTeX 项目迁移到 Quarto 的学者：无需手动编辑 JSON，即可带上原有文献库。",
    bibtexToWordTitle: "BibTeX 转 Word 转换器",
    bibtexToWordMetaDescription:
      "将 .bib 文件转换为带有 APA 格式、悬挂缩进参考文献列表的 Word 文档。免费——解析在浏览器中完成。",
    bibtexToWordIntro:
      "当 .bib 文件需要变成一份文档，而不是另一种数据格式时，这个转换器会在本地解析你的 BibTeX，将每个条目按 APA 第 7 版排版，并下载一份已按字母顺序排列、并设置好悬挂缩进的参考文献列表 Word 文件。",
    bibtexToWordUseCase:
      "流程末端常见的一步：期刊已接收论文，但要求提供可编辑的 Word 格式最终参考文献列表，而你的原始数据是 BibTeX。",
    risToApaTitle: "RIS 转 APA 转换器",
    risToApaMetaDescription:
      "粘贴或上传 RIS 导出文件，即可获得 APA 第 7 版参考文献列表。兼容 Zotero、Mendeley、EndNote 以及数据库导出文件——免费。",
    risToApaIntro:
      "几乎每个数据库的“导出”按钮都会生成 RIS 文件，而作业要求的是 APA 格式。粘贴导出内容（或上传 .ris 文件），每条 TY/AU/TI 记录都会在浏览器中解析并渲染为 APA 第 7 版参考文献——无需安装任何文献管理软件。",
    risToApaUseCase:
      "图书馆数据库的捷径：将检索结果导出为 RIS，在这里转换，省去为一篇一次性论文专门配置 Zotero 的麻烦。",
    risToMlaTitle: "RIS 转 MLA 转换器",
    risToMlaMetaDescription:
      "将数据库或文献管理软件导出的 RIS 转换为 MLA 第 9 版引用文献条目。粘贴或上传即可——免费，无需注册。",
    risToMlaIntro:
      "JSTOR、ProQuest 和 EBSCO 都导出 RIS 格式，而英语系无一例外都要求 MLA。这个页面正好填补这个空缺——记录在本地解析，并按照 MLA 第 9 版核心要素模板渲染，可直接粘贴到你的引用文献列表页面。",
    risToMlaUseCase:
      "专为论文交稿前夜准备：从 JSTOR 导出的十篇 citations.ris 文献，完全不想手动排版时使用。",
    risToBibtexTitle: "RIS 转 BibTeX 转换器",
    risToBibtexMetaDescription:
      "将 RIS 转换为适用于 LaTeX 和 Overleaf 的 BibTeX。粘贴或上传 .ris 导出文件，下载一个干净的 .bib 文件——免费，浏览器内完成。",
    risToBibtexIntro:
      "转向 LaTeX 方向时：文献管理软件和数据库都使用 RIS，而论文构建需要 .bib。所有记录都在浏览器中完成转换——包括从 TY 标签映射类型、生成引用键——最终得到一个 Overleaf 可以直接接受的文件。",
    risToBibtexUseCase:
      "多见于合作场景：使用 Zotero 的一方导出 RIS，使用 LaTeX 的一方通过这个页面处理，双方都能继续使用自己惯用的工具。",
    risToCslJsonTitle: "RIS 转 CSL-JSON 转换器",
    risToCslJsonMetaDescription:
      "将 RIS 导出文件转换为适用于 Pandoc 和 Quarto 流程的 CSL-JSON。粘贴或上传 .ris 文件，下载 references.json——免费。",
    risToCslJsonIntro:
      "对 Pandoc 和 Quarto 用户来说，CSL-JSON 是原生的文献列表格式。把数据库或管理软件导出的 RIS 交给这个页面，就能得到一个结构化的 JSON 数组——所有记录都在本地解析，不会上传到任何地方。",
    risToCslJsonUseCase:
      "适合 docs-as-code 工作流：审稿人发来 RIS 附件，而仓库里的引用文件是 references.json，这个页面就是两者之间的转接器。",
    cslJsonToBibtexTitle: "CSL-JSON 转 BibTeX 转换器",
    cslJsonToBibtexMetaDescription:
      "将 CSL-JSON（来自 Pandoc/Quarto/Zotero 的导出文件）转换为 BibTeX。粘贴或上传 .json 文件，下载 .bib 文件——免费，浏览器内完成。",
    cslJsonToBibtexIntro:
      "有时流程也会反过来——诞生于 Pandoc 或 Zotero 流程中的文献库，需要供给 LaTeX 构建使用。这个转换器会在浏览器中读取 CSL-JSON 数组，并根据 CSL 模型把类型和字段重新映射为 BibTeX 条目。",
    cslJsonToBibtexUseCase:
      "适合合著者坚持使用 Overleaf 的 Quarto 作者：将 references.json 保留为权威版本，需要时再按需生成 .bib 文件。",
    cslJsonToRisTitle: "CSL-JSON 转 RIS 转换器",
    cslJsonToRisMetaDescription:
      "将 CSL-JSON 转换为 RIS，方便 Zotero、Mendeley 或 EndNote 导入。粘贴或上传 .json 文件，下载 .ris 文件——免费。",
    cslJsonToRisIntro:
      "CSL-JSON 在 Pandoc 流程中如鱼得水，却难以被大多数文献管理软件的导入对话框识别。转换为 RIS 后，同样的记录就能被普遍导入——每一项都会在浏览器中映射为 TY/AU/TI 标签，不会发送到任何服务器。",
    cslJsonToRisUseCase:
      "解决交接难题：你的文献库是 git 仓库中的 references.json，而导师却要求你在周五前提供“EndNote 格式”的版本。",
  },

  // The infinite-scroll style list shared by the country and subject pages and by the
  // /citation-styles hub. It is a client component, so these arrive as a prop rather
  // than through `getDictionary` — pulling all 17 dictionaries into the browser bundle
  // to label a search box is the thing `link-path.ts` exists to avoid.
  stylesBrowser: {
    defaultTitle: "全部引用样式",
    styleSingular: "种样式",
    stylePlural: "种样式",
    found: "，符合搜索条件",
    filterPlaceholder: "筛选样式...",
    noMatches: "没有符合搜索条件的样式。",
    loadingMore: "正在加载更多样式...",
    loadMore: "加载更多（还剩 {count} 个）",
    showingAll: "已显示全部 {count} 种样式",
    internationalLabel: "国际通用",
  },

  footer: {
    tagline:
      "支持 {count} 种样式的免费参考文献生成器。无需账号，无需付费订阅，也没有次数限制。",
    freeTools: "免费工具",
    resources: "资源",
    legal: "法律条款",
    language: "语言",
    languageLabel: "选择语言",
    rights: "参考文献依据 Citation Style Language 的官方定义排版。",
    popularStyles: "常用样式",
    about: "关于",
    privacy: "隐私",
    terms: "条款",
    contact: "联系",
    copyright: "© {year} CitationEasy，免费使用。",
    poweredBy: "技术支持：",
    allTools: "全部写作工具",
    citationGuides: "引用指南",
    allStyles: "全部引用样式",
    citationGlossary: "引用术语表",
    howToCiteAny: "如何引用任何来源",
  },

  faq: {
    heading: "常见问题",
    homeQ1: "CitationEasy 真的免费吗？",
    homeA1:
      "是的。CitationEasy 完全免费，无需注册，没有次数限制，也没有付费版本。全部 {count} 种引用样式对所有人开放。",
    homeQ2: "支持哪些引用样式？",
    homeA2:
      "支持 Citation Style Language 项目的全部 {count} 种样式，包括 APA 第 7 版、MLA 第 9 版、Chicago、Harvard、Vancouver、IEEE、AMA 和 Turabian。",
    homeQ3: "需要注册账号吗？",
    homeA3:
      "不需要。CitationEasy 完全在浏览器中运行，参考文献列表保存在你自己的设备上。没有任何内容上传到服务器，也没有账号可注册。",
    homeQ4: "可以用我自己的语言生成参考文献吗？",
    homeA4:
      "可以。参考文献的语言是独立于界面语言的设置，因此你可以用中文操作，同时按论文所用语言输出参考文献。",
    styleQ1: "{style} 引用生成器免费吗？",
    styleA1:
      "免费。生成 {style} 参考文献不限次数，也无需账号。粘贴 DOI、网址、ISBN 或 BibTeX 条目，即可复制排好版的文献条目。",
    styleQ2: "如何用 {style} 引用一个来源？",
    styleA2:
      "把来源的 DOI、网址、ISBN 或 BibTeX 条目粘贴到本页的生成器中。{style} 已经预先选好，参考文献条目和文内引用会立即显示。",
    styleQ3: "{style} 的排版有多准确？",
    styleA3:
      "每条文献都由 Citation Style Language 项目发布的官方 {style} 定义生成——与 Zotero、Mendeley 使用的是同一个文件——而不是对规则的近似模仿。",
  },

  tool: {
    modeLabel: "引用录入方式",
    modeAuto: "URL / DOI / ISBN",
    modeBook: "搜索图书",
    modeArticles: "搜索论文",
    modeManual: "手动录入",
    errorEnterManually: "手动填写信息",
    autoLabel: "粘贴 URL、DOI、PMID、arXiv ID、ISBN 或 BibTeX",
    autoHint:
      "我们会自动识别类型并获取元数据。每行粘贴一个标识符，即可一次生成整份列表。",
    generate: "生成引用",
    generating: "生成中…",
    bookSearchLabel: "按书名、作者或 ISBN 搜索",
    bookSearchEmpty: "暂无结果。点击“搜索”查找图书。",
    bookSearchSource: "搜索收录数百万册图书的 Open Library 目录",
    worksSearchLabel: "按论文标题或作者搜索",
    worksSearchEmpty: "暂无结果。点击“搜索”查找论文。",
    worksSearchSource: "搜索 Crossref 目录：期刊论文、会议论文与图书章节",
    showingResults: "显示 {total} 条结果中的 {shown} 条",
    search: "搜索",
    searching: "搜索中…",
    bulkResults: "批量引用结果",
    copy: "复制",
    copied: "已复制",
    copyHint: "复制并粘贴到你的论文中",
    resultTitle: "已排版的引用",
    edit: "编辑",
    completeFields: "补全",
    fieldsIncomplete: "other:{count} 个字段不完整",
    outputLanguage: "语言",
    sourceType: "文献类型",
    fieldAuthors: "作者",
    fieldAuthorsHint: "张三 或 Zhang, San",
    fieldTitle: "论文或图书标题",
    fieldBookTitle: "书名",
    fieldBookTitleHint: "图书或文集的名称",
    fieldEditors: "编者",
    fieldJournal: "期刊 / 出版物",
    fieldJournalHint: "期刊或出版物名称",
    fieldPublisher: "出版社 / 网站",
    fieldPublisherHint: "出版社名称或网站",
    fieldDate: "日期 / 年份",
    fieldVolume: "卷",
    fieldIssue: "期",
    fieldPages: "页码",
    fieldFormat: "格式 / 载体",
    fieldAuthorsGroup: "团体或机构作者",
    fieldAuthorsGroupHint:
      "若要与个人并列填写团体，请用花括号括起：{R Core Team}, 张三",
    fieldTranslators: "译者",
    fieldInterviewers: "采访者",
    fieldVersion: "版本",
    fieldCourt: "法院",
    fieldIssuingAuthority: "颁发机构",
    fieldPatentNumber: "专利号",
    fieldEpisodeNumber: "集数",
    fieldLawNumber: "法律或法案编号",
    fieldReportNumber: "报告编号",
    fieldReviewedTitle: "被评作品标题",
    fieldReviewedAuthors: "被评作品作者",
    inTextCitation: "文内引用",
    quickEdit: "快速编辑",
    quickEditHint: "更新元数据并重新生成引用。",
    update: "更新",
    saving: "保存中…",
    fieldAuthor: "作者",
    fieldDateShort: "日期",
    fieldTitleShort: "标题",
  },

  toolCollections: {
    "citation-generators": "引文生成器",
    "citation-tools": "引文与参考文献工具",
    "paper-formatting": "论文格式",
    "essay-writing": "论文与文章写作",
    "writing-analysis": "写作分析与修改",
    "word-counters": "计数器与可读性",
    vocabulary: "词汇与查词",
    "research-tools": "研究与发表",
    statistics: "统计计算器",
    math: "数学计算器",
    science: "科学与实验计算器",
    charts: "图表与示意图",
    economics: "经济、商业与金融",
    grades: "GPA 与成绩计算器",
    study: "学习与效率",
    classroom: "课堂与打印材料",
    random: "随机生成与抽取",
    "text-tools": "文本清理与格式",
    "fancy-text": "花式文字与符号",
    language: "语言与转写",
    ciphers: "编码与密码",
    converters: "单位、日期与数字转换",
    developer: "数据、标记语言与代码",
  },
  bib: {
    title: "我的参考文献列表",
    studioTitle: "参考文献工作室",
    studioProjects: "项目",
    studioExplore: "探索",
    studioExploreIntro: "指南、工具和所有引用样式，一键直达。",
    studioStyleGuide: "{style} 样式指南",
    studioStyleGuideHint: "规则、示例和文内引用格式",
    studioGenerator: "引用生成器",
    studioNewProject: "新建项目",
    studioSwitchProject: "切换项目",
    studioAddSource: "添加来源",
    studioAddSourceHint:
      "粘贴 DOI、URL 或 ISBN，搜索图书和论文，或手动填写信息。你保存的所有内容都会归入此项目。",
    studioDoneAdding: "完成添加",
    studioReferences: "参考文献",
    studioReferenceCount: "{count} 条参考文献",
    studioInspectorTitle: "参考文献详情",
    studioInspectorEmpty: "选择一条参考文献以查看文内引用、备注和来源详情。",
    studioSavedLocally: "已保存在此浏览器",
    studioOpen: "打开参考文献工作室",
    studioViewInStudio: "在工作室中查看",
    studioFooterSaved: "参考文献工作室已保存 {count} 条",
    studioFooterEmpty: "保存的引用会在工作室中汇成完整的参考文献列表",
    studioClearSelection: "清除选择",
    studioDeleteSelected: "删除所选",
    studioDeleteSelectedBody: "从“{name}”删除 {count} 条引用？此操作无法撤销。",
    studioDeletedMany: "已删除 {count} 条引用",
    studioMoveTo: "移动到",
    studioCopyTo: "复制到",
    studioSearchShortcut: "按 / 搜索",
    studioStyle: "样式",
    studioCopyEntry: "复制条目",
    studioEntryCopied: "条目已复制",
    studioSourceDetails: "来源详情",
    studioEmptyTitle: "此项目为空",
    studioEmptyBody:
      "在上方添加第一条来源，或导入现有的 BibTeX、RIS 或 CSL-JSON 库。",
    studioBackup: "备份",
    sharedTitle: "共享的参考文献列表",
    save: "保存",
    saving: "保存中…",
    saved: "已保存",
    searchPlaceholder: "搜索引用…",
    empty: "尚未保存任何引用",
    emptySearch: "没有与搜索匹配的引用。",
    countIn: "{name}：{count} 条",
    savedOn: "保存于 {date}",
    emptyInBib:
      "此参考文献列表中还没有引用。生成并保存引用，或导入现有的 BibTeX、RIS 或 CSL-JSON 库。",
    savedToast: "已将引用保存到参考文献列表",
    copyAll: "全部复制",
    copyAllTitle: "复制全部引用",
    export: "导出",
    exportTitle: "导出参考文献列表",
    import: "导入",
    importTitle: "导入文献",
    importLabel: "导入文献（BibTeX、RIS 或 CSL-JSON）",
    importing: "正在导入…",
    importFailed: "导入失败，请重试。",
    importSummary:
      "已导入：{added} · 已跳过重复项：{duplicates} · 超出上限：{overLimit} · 失败：{failed}",
    importUnreadable: "无法读取文件。",
    importUnparseable: "无法导入这些文献。",
    importReviewTitle: "检查导入内容",
    importReviewIntro: "请在格式化和保存前检查 {total} 条 {format} 文献。",
    importReviewCount: "已包含 {included}/{total} 条",
    importSelectAll: "全部包含",
    importExcludeAll: "全部排除",
    importLimitNotice: "超出 200 条上限的 {count} 条文献不会被导入。",
    importDuplicateExisting: "可能与已保存的“{title}”重复",
    importDuplicateBatch: "可能与本次导入中的“{title}”重复",
    importMissingField: "缺少{field}",
    importUntitled: "无标题文献",
    importReview: "检查文献",
    importCommit: "格式化并导入",
    importBack: "返回",
    importOutcomeTitle: "导入完成",
    importOutcomeSummary:
      "已添加：{added} · 已跳过重复项：{duplicates} · 已排除：{excluded} · 超出上限：{overLimit} · 失败：{failed}",
    sortBy: "排序方式",
    sortTitle: "排序参考文献列表",
    sortAuthor: "作者（A–Z）",
    sortNewest: "添加时间（最新）",
    sortOldest: "年份（最早优先）",
    sortManual: "手动排序",
    backupAll: "完整项目备份（.json）",
    backupDownloaded: "完整项目备份已下载",
    backupUnreadable: "无法读取备份文件。",
    restore: "恢复",
    restoreTitle: "恢复所有参考文献项目",
    restoreWarning:
      "系统会先验证备份，然后替换此浏览器中当前存储的所有参考文献。此操作无法撤销。",
    restoreChoose: "选择 CitationEasy 备份（.json）",
    replaceAndRestore: "替换并恢复",
    restoreComplete: "所有参考文献项目已恢复",
    restoreInvalid: "这不是有效的 CitationEasy 项目备份。",
    selectAllVisible: "选择所有显示项",
    selectedCount: "已选择 {count} 项",
    clusterOpen: "合并引用",
    clusterTitle: "复合文内引用",
    clusterDescription:
      "将所选参考文献生成一个引用组。所选样式会控制顺序、标点和编号。",
    clusterTarget: "输出",
    clusterLocatorOptional: "可选定位信息",
    clusterBuildCopy: "生成并复制引用",
    clusterBuilding: "正在生成引用…",
    clusterCopied: "已复制复合引用",
    clusterFailed: "无法生成复合引用，请重试。",
    destination: "目标参考文献",
    chooseDestination: "选择参考文献…",
    copySelected: "复制",
    moveSelected: "移动",
    copyComplete: "已复制 {count} 条引用。已跳过 {skipped} 条重复项。",
    moveComplete: "已移动 {count} 条引用。{skipped} 条重复项保留在来源中。",
    selectCitation: "选择 {title}",
    moveUp: "上移",
    moveDown: "下移",
    deleteTitle: "删除引用",
    deleteConfirm: "删除该引用？",
    deleted: "已删除引用",
    editTitle: "编辑引用",
    close: "关闭",
    copiedAll: "已复制全部引用",
    copiedAnnotated: "已复制带注释的参考文献列表",
    copiedBibtex: "已复制 BibTeX",
    copiedBulleted: "已复制项目符号列表",
    copiedNumbered: "已复制编号列表",
    copiedHtml: "已复制 HTML 片段",
    downloadedWord: "已下载 Word 文档",
    downloadedRtf: "已下载 RTF 文件",
    downloadedRis: "已下载 RIS 文件",
    downloadedCsl: "已下载 CSL-JSON 文件",
    downloadedAnnotated: "已下载带注释的参考文献列表",
    copyFailed: "无法复制到剪贴板",
    shareCopied: "已复制链接！拿到链接的人都可以查看并导入此列表。",
    shareFailed: "无法创建分享链接",
    regenerateFailed: "无法重新生成该引用",
    updateFailed: "无法更新该引用",
    fieldAuthor: "作者",
    fieldTitle: "标题",
    fieldDate: "日期",
    fieldPublisher: "出版社",
    fieldVolume: "卷",
    fieldIssue: "期",
    fieldPages: "页码",
    fieldUrl: "URL",
    fieldAccessed: "访问日期",
    fieldEditor: "编辑",
    fieldEvent: "活动地点",
    noteLabel: "对该文献的注释",
    noteEdit: "编辑此注释",
    notePlaceholder: "概括这篇文献、作出评价，并说明它与你论文的关系…",
    noteRemoved: "已删除注释",
    noteSave: "保存注释",
    noteCancel: "取消",
    noteAdd: "添加注释",
    noteWords: "{count} 个词",
    noteSaved: "已保存注释（{count} 个词）",
    noteClickToEdit: "点击编辑",
    inTextCopy: "复制该文献的文内引用",
    inTextSelect: "选中下方的引用即可复制。",
    inTextFailed: "无法生成文内引用，请重试。",
    locator: "定位信息",
    locatorType: "定位类型",
    locatorPage: "页",
    locatorChapter: "章",
    locatorSection: "节",
    locatorParagraph: "段",
    locatorLine: "行",
    locatorVerse: "诗节",
    reformat: "重新排版",
    reformatTitle: "用另一种格式重排全部引用",
    reformatHeading: "将全部内容统一为一种格式",
    reformatChoose: "选择一种格式…",
    reformatFailed: "重新排版失败，你的参考文献列表未改变。",
    sortOrder: "排序依据",
    copyToClipboard: "复制到剪贴板",
    numberedList: "编号列表",
    bulletedList: "项目符号列表",
    htmlSnippet: "HTML 片段",
    copyWithAnnotations: "连同注释一起复制",
    wordAnnotated: "带注释的 Word 文档（.doc）",
    downloadFile: "下载文件",
    wordDocument: "Word 文档（.doc）",
    copyShareLink: "复制分享链接",
    print: "打印…",
    printFailed: "弹窗已被阻止，请允许弹窗以便打印",
    font: "字体",
    sitePublication: "网站 / 出版物名称",
    importIntro:
      "粘贴或上传 BibTeX（.bib）、RIS（.ris）或 CSL-JSON（.json）文件",
    importUpload: "上传 .bib、.ris 或 .json 文件",
    importPaste: "或粘贴 BibTeX、RIS 或 CSL-JSON",
    cancel: "取消",
    done: "完成",
    delete: "删除",
    deleteBody: "该引用将从你的参考文献列表中永久删除。",
    editBody: "更新元数据并保存，即可重新生成引用。",
    reformatExplain:
      "根据已保存的文献数据重建全部 {count} 条 — 不会重新下载，也不会丢失任何文献。",
    inTextHeading: "文内引用",
    inTextLocatorHint:
      "添加页码或其他定位信息，可将引用指向文献中的具体位置——直接引用时多数格式都要求这样做。留空则使用简单形式。",
    inTextCopyButton: "复制文内引用",
    mixedStyles: "此参考文献列表混用了 {count} 种引用格式（{styles}）。",
    mixedStylesAction: "提交前请使用{action}将它们统一为一种格式。",
    selectBibliography: "选择参考文献列表",
    newBibliography: "新建参考文献列表",
    moreOptions: "更多选项",
    rename: "重命名",
    removeDuplicates: "删除重复项",
    cannotDeleteOnly: "无法删除唯一的参考文献列表",
    newBibliographyBody: "为另一篇论文或项目创建参考文献列表。",
    bibliographyName: "参考文献列表名称",
    create: "创建",
    renameBibliography: "重命名参考文献列表",
    renameBibliographyBody: "请输入“{name}”的新名称。",
    newName: "新名称",
    duplicateRemovedOne: "已删除 {count} 个重复项",
    duplicateRemovedMany: "已删除 {count} 个重复项",
    noDuplicates: "未发现重复参考文献",
    removeDuplicatesConfirm: "删除重复参考文献？",
    removeDuplicatesBody:
      "系统会在“{name}”中按 DOI、ISBN，或标题、年份和作者识别相同来源，并保留一份。无法匹配的参考文献不会更改。",
    deleteBibliographyConfirm: "删除参考文献列表？",
    deleteBibliographyBody: "这将永久删除“{name}”及其中的全部 {count} 条引用。",
  },

  bibliographyPage: {
    metaTitle: "参考文献工作室 - 整理、批注与导出引用",
    metaDescription:
      "在浏览器中创建并管理私密参考文献项目。无需账户即可搜索、排序、批注、编辑、重新格式化、导入、导出和分享。",
    keyword1: "参考文献生成器",
    keyword2: "引用管理器",
    keyword3: "注释参考文献工具",
    ogTitle: "参考文献工作室",
    ogDescription: "用于整理、格式化、批注、导入和导出引用的私密全页面工作区。",
    ogImageTitle: "参考文献工作室",
    ogImageSubtitle: "整理、批注、重新格式化、导出并分享参考文献。",
    ogKind: "引用工作区",
    breadcrumb: "参考文献工作室",
    eyebrow: "浏览器中的私密工作区",
    lead: "在一处管理论文的全部参考文献。除非你主动导出或分享，否则项目只保存在此浏览器中。",
    privacyTitle: "默认私密",
    privacyBody:
      "项目和批注使用浏览器本地存储。分享链接包含引用，但会有意排除私人笔记。",
    workflowTitle: "从来源列表到最终论文",
    workflowBody:
      "切换项目，搜索和排序参考文献，添加批注或定位信息，修正元数据，并将整个列表统一为一种引用格式。",
    formatsTitle: "导入参考文献并随处使用",
    formatsBody:
      "导入 BibTeX、RIS 或 CSL-JSON。复制已格式化列表，或导出 Word、RTF、RIS、CSL-JSON、HTML、BibTeX 和批注版本。",
  },

  // The 95-term glossary: /glossary and /glossary/[slug].
  //
  // The chrome only. Each term's own prose — `short`, the `body` paragraphs, the
  // example — is ~10,600 words that cannot live in a flat string map and must not be
  // copied verbatim into sixteen files; it lives in `src/content/glossary/<locale>.json`
  // and is read through `lib/localized-content.ts`.
  glossaryPage: {
    metaTitle: "{term} - 定义与示例",
    notFoundTitle: "未找到该术语",
    keywordDefinition: "{term}的定义",
    keywordWhatIs: "什么是{term}",
    ogTitle: "{term} - CitationEasy 术语表",
    ogKind: "引用术语表",
    breadcrumb: "术语表",
    alsoCalled: "又称：{aliases}",
    doItHeading: "在 CitationEasy 上操作",
    seeAlsoHeading: "另请参阅",
    moreInHeading: "{category}分类下的更多术语",
    navLabel: "术语表导航",
    allTermsLink: "全部 {count} 个术语",
    generatorLink: "免费引用生成器",
    termSetName: "CitationEasy 引用术语表",
    hubMetaTitle: "引用术语表 - {count} 个术语详解",
    hubMetaDescription:
      "用通俗语言解释 {count} 个引用与学术写作术语：et al.、ibid.、DOI、悬挂缩进、拼凑抄写、正式版本等——每个术语都配有示例。",
    hubKeyword1: "引用术语表",
    hubKeyword2: "引用术语",
    hubKeyword3: "学术写作术语表",
    hubKeyword4: "et al 是什么意思",
    hubKeyword5: "什么是 DOI",
    hubKeyword6: "参考文献术语",
    hubOgTitle: "引用术语表",
    hubOgDescription: "{count} 个引用与学术写作术语的定义，附示例。",
    hubOgImageTitle: "引用术语表",
    hubOgImageSubtitle: "{count} 个引用与学术写作术语，通俗易懂，配有示例。",
    hubOgKind: "参考资料",
    hubTermSetDescription: "{count} 个引用、参考文献和学术写作术语的定义。",
    hubCollectionName: "引用术语表",
    hubCollectionDescription: "用通俗语言解释 {count} 个引用与学术写作术语。",
    hubHeading: "引用术语表",
    hubIntro:
      "{count} 个来自引用与学术写作的术语，用通俗语言解释并配有示例——包括样式指南中出现的缩写、参考文献的组成部分、用于精确定位来源的标识符，以及学术出版领域的常用词汇。",
    hubJumpLabel: "跳转到字母",
    hubBrowseHeading: "按主题浏览",
    hubAllHeading: "全部术语（A–Z）",
    hubWorkHeading: "在实践中运用这些术语",
    hubWorkGenerator: "免费引用生成器",
    hubWorkStyles: "引用样式指南",
    hubWorkHowTo: "如何引用任何来源",
    hubWorkCompare: "比较引用样式",
    hubWorkTools: "写作与引用工具",
    hubWorkFaq: "引用常见问题",
  },

  // /faq — the 17-question hub. The questions and answers themselves are in
  // `src/data/faq.ts` (the one source the API, the MCP tool and /md also read); this is
  // the page around them.
  faqPage: {
    metaTitle: "引用常见问题 - 常见引用问题快速解答",
    metaDescription:
      "针对学生实际会问的引用问题给出快速、准确的解答：DOI、无作者来源、et al.、悬挂缩进、引用 AI、抄袭规则、APA 与 MLA 的区别等——并附完整指南链接。",
    ogTitle: "引用常见问题",
    ogDescription:
      "针对最常见引用问题的快速解答，从 DOI、et al. 到如何引用 ChatGPT。",
    ogImageTitle: "引用问答",
    ogImageSubtitle: "针对学生实际会问的引用问题，给出简短准确的解答。",
    ogKind: "常见问题",
    breadcrumbLabel: "面包屑导航",
    breadcrumbHome: "首页",
    breadcrumbCurrent: "引用常见问题",
    heading: "引用常见问题",
    intro:
      "针对学生实际会问的引用问题给出快速、准确的解答——需要深入了解时，还附有完整指南或工具的链接。",
    contentsLabel: "常见问题目录",
    stillStuckHeading: "还没解决？",
    stillStuckBody:
      "浏览[完整指南库](/guides)、查看[样式速查表](/cheat-sheets)、在[引用术语表](/glossary)中查找术语、[搜索全站](/search)，或直接[生成引用](/)，让生成器为你套用规则。",
  },

  // /cheat-sheets and its three style sheets. The citation FORMATS in them
  // ("Author, A. A. (Year)…") are the styles' own templates and stay as they are in
  // every language, exactly as the per-style examples do; the labels around them do not.
  cheatSheets: {
    hubMetaTitle: "引用样式速查表 - 快速参考指南",
    hubMetaDescription:
      "APA、MLA 和 Chicago 引用样式的快速参考速查表。适合打印的指南，附常见文献类型示例。",
    hubBreadcrumbHome: "首页",
    hubBreadcrumbCurrent: "引用样式速查表",
    hubBackLink: "← 返回生成器",
    hubHeading: "引用样式速查表",
    hubIntro: "最常用引用样式的快速参考指南。适合打印，且始终保持最新。",
    hubApaTitle: "APA 第 7 版速查表",
    hubApaDesc: "心理学、社会科学、教育学、护理学",
    hubMlaTitle: "MLA 第 9 版速查表",
    hubMlaDesc: "文学、语言学科、人文学科、文化研究",
    hubChicagoTitle: "Chicago 样式速查表",
    hubChicagoDesc: "历史学、艺术史、哲学、宗教学",
    backToSheets: "← 返回速查表",
    generalRules: "通用规则",
    inTextCitations: "文内引用",
    colSituation: "情形",
    colFormat: "格式",
    colExample: "示例",
    book: "图书",
    journalArticle: "期刊论文",
    website: "网站",
    apaMetaTitle: "APA 第 7 版速查表 - 快速参考指南",
    apaMetaDescription:
      "APA 第 7 版引用速查表，附图书、期刊、网站等的示例。适合打印的快速参考。",
    apaHeading: "APA 第 7 版速查表",
    apaLead: "最常见 APA 引用格式的快速参考。",
    apaRule1: "使用**悬挂缩进**（首行顶格，后续各行缩进 0.5 英寸）",
    apaRule2: "所有条目使用双倍行距",
    apaRule3: "按作者姓氏的字母顺序排列",
    apaRule4: "图书、期刊和卷名的标题使用斜体",
    apaRule5: "如有 DOI，务必使用",
    apaRow1Situation: "单作者",
    apaRow1Format: "(Author, Year)",
    apaRow1Example: "(Smith, 2024)",
    apaRow2Situation: "两位作者",
    apaRow2Format: "(Author & Author, Year)",
    apaRow2Example: "(Smith & Jones, 2024)",
    apaRow3Situation: "三位及以上作者",
    apaRow3Format: "(First Author et al., Year)",
    apaRow3Example: "(Smith et al., 2024)",
    apaRow4Situation: "直接引语",
    apaRow4Format: "(Author, Year, p. #)",
    apaRow4Example: "(Smith, 2024, p. 45)",
    apaRow5Situation: "无作者",
    apaRow5Format: '("Title," Year)',
    apaRow5Example: '("Study Results," 2024)',
    apaReferenceHeading: "参考文献列表示例",
    apaBookExample:
      "Author, A. A. (Year). *Title of work: Capital letter also for subtitle*. Publisher. https://doi.org/xxxxx",
    apaJournalExample:
      "Author, A. A., & Author, B. B. (Year). Title of article. *Title of Periodical, Volume*(Issue), Page–Page. https://doi.org/xxxxx",
    apaWebsiteExample:
      "Author, A. A. (Year, Month Day). *Title of page*. Site Name. https://www.example.com/page",
    apaChapterLabel: "编者图书章节",
    apaChapterExample:
      "Author, A. A. (Year). Title of chapter. In E. E. Editor (Ed.), *Title of book* (pp. #–#). Publisher.",
    apaConferenceLabel: "会议论文",
    apaConferenceExample:
      "Author, A. A. (Year, Month Days). *Title of paper* [Paper presentation]. Conference Name, Location.",
    apaDifferencesHeading: "与 APA 第 6 版的主要区别",
    apaDifference1: "网址前不再加“Retrieved from”",
    apaDifference2: "DOI 需以 https://doi.org/ 链接格式呈现",
    apaDifference3: "最多列出 20 位作者后再使用省略号",
    apaDifference4: "不再要求标注出版社所在地",
    apaDifference5: "使用单数“they”表示性别中立",
    apaCtaText: "自动生成完美的 APA 引用",
    apaCtaButton: "APA 引用生成器 →",
    mlaMetaTitle: "MLA 第 9 版速查表 - 快速参考指南",
    mlaMetaDescription:
      "MLA 第 9 版引用速查表，附图书、文章、网站等的示例。适合打印的快速参考。",
    mlaHeading: "MLA 第 9 版速查表",
    mlaLead: "最常见 MLA 引用格式的快速参考。",
    mlaRule1: "使用**悬挂缩进**",
    mlaRule2: "所有条目使用双倍行距",
    mlaRule3: "按作者姓氏的字母顺序排列",
    mlaRule4: "页面标题为“Works Cited”（引用文献）",
    mlaRule5: "容器标题（图书、期刊、网站）使用斜体",
    mlaRow1Situation: "单作者",
    mlaRow1Format: "(Author Page)",
    mlaRow1Example: "(Smith 45)",
    mlaRow2Situation: "两位作者",
    mlaRow2Format: "(Author and Author Page)",
    mlaRow2Example: "(Smith and Jones 45)",
    mlaRow3Situation: "三位及以上作者",
    mlaRow3Format: "(First Author et al. Page)",
    mlaRow3Example: "(Smith et al. 45)",
    mlaRow4Situation: "无作者",
    mlaRow4Format: '("Title" Page)',
    mlaRow4Example: '("Study Results" 45)',
    mlaRow5Situation: "无页码",
    mlaRow5Format: "(Author)",
    mlaRow5Example: "(Smith)",
    mlaWorksCitedHeading: "引用文献示例",
    mlaBookExample: "Last, First. *Title of Book*. Publisher, Year.",
    mlaJournalExample:
      'Last, First. "Title of Article." *Journal Title*, vol. #, no. #, Year, pp. #–#.',
    mlaWebsiteExample:
      'Last, First. "Title of Page." *Website Name*, Day Month Year, URL.',
    mlaAnthologyLabel: "文集/编者图书",
    mlaAnthologyExample:
      'Last, First. "Title of Essay." *Title of Collection*, edited by First Last, Publisher, Year, pp. #–#.',
    mlaCoreHeading: "MLA 核心要素（按顺序）",
    mlaCore1: "Author.",
    mlaCore2: '"Title of Source."',
    mlaCore3: "*Title of Container*,",
    mlaCore4: "Contributors,",
    mlaCore5: "Version,",
    mlaCore6: "Number,",
    mlaCore7: "Publisher,",
    mlaCore8: "Publication date,",
    mlaCore9: "Location (pages, URL, DOI).",
    mlaCtaText: "自动生成完美的 MLA 引用",
    mlaCtaButton: "MLA 引用生成器 →",
    chicagoMetaTitle: "Chicago 样式速查表 - 快速参考指南",
    chicagoMetaDescription:
      "Chicago 手册引用速查表，附注释-文献目录制和著者-出版年制示例。适合打印的快速参考。",
    chicagoHeading: "Chicago 样式速查表",
    chicagoLead: "Chicago 手册（第 18 版）引用格式的快速参考。",
    chicagoSystemsHeading: "Chicago 的两种体系",
    chicagoColNb: "注释-文献目录制（NB）",
    chicagoColAd: "著者-出版年制（AD）",
    chicagoRow1Nb: "脚注/尾注 + 文献目录",
    chicagoRow1Ad: "括号引用 + 参考文献列表",
    chicagoRow2Nb: "历史学、艺术、人文学科",
    chicagoRow2Ad: "自然科学、社会科学",
    chicagoRow3Nb: "首次出现的注释更为详细",
    chicagoRow3Ad: "与 APA 格式相似",
    chicagoExamplesHeading: "注释-文献目录制示例",
    chicagoNoteLabel: "注释：",
    chicagoBibliographyLabel: "文献目录：",
    chicagoBookLabel: "图书（脚注）",
    chicagoBookNote: "1. First Last, *Title of Book* (Publisher, Year), page.",
    chicagoBookBibliography: "Last, First. *Title of Book*. Publisher, Year.",
    chicagoJournalLabel: "期刊论文（脚注）",
    chicagoJournalNote:
      '1. First Last, "Title of Article," *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoJournalBibliography:
      'Last, First. "Title of Article." *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoWebsiteLabel: "网站（脚注）",
    chicagoWebsiteNote:
      '1. First Last, "Title of Page," Site Name, Month Day, Year, URL.',
    chicagoWebsiteBibliography:
      'Last, First. "Title of Page." Site Name. Month Day, Year. URL.',
    chicagoShortHeading: "简化注释",
    chicagoShortIntro: "在首次完整引用之后，可使用简化脚注：",
    chicagoShortExample: "1. Last, *Short Title*, page.",
    chicagoRulesHeading: "关键排版规则",
    chicagoRule1: "正文中的脚注标记使用上标数字",
    chicagoRule2: "文献目录条目使用悬挂缩进",
    chicagoRule3: "同一文献连续引用时，使用简化脚注，而不是“Ibid.”（同上）",
    chicagoRule4: "标题：图书和期刊使用斜体，文章和章节标题加引号",
    chicagoRule5: "内容可能变化的在线来源应注明访问日期",
    chicagoCtaText: "自动生成完美的 Chicago 引用",
    chicagoCtaButton: "Chicago 引用生成器 →",
  },

  // The catalog's page-type labels (`CATALOG_TYPE_LABELS`), which the search page shows
  // as facet chips and beside every result. Layer 2: a fact from `src/lib/**` rather
  // than page chrome, and English until it is mapped through here.
  catalogTypes: {
    core: "核心页面",
    styleLanding: "主要引用样式专题页",
    style: "引用样式参考页",
    styleExamples: "引用示例页",
    styleFormat: "论文排版指南",
    styleInText: "文内引用指南",
    styleAnnotatedBibliography: "带注释文献表指南",
    styleReferencePage: "参考文献/引用文献页指南",
    tool: "免费写作与引用工具",
    guide: "学习指南",
    guideCategory: "指南分类",
    toolCollection: "工具合集",
    howToCite: "如何用<style>引用<source>",
    comparison: "引用样式比较",
    converter: "标识符与格式转换工具",
    field: "按学科分类的引用样式",
    region: "按国家/地区分类的引用样式",
    category: "按主题分类的引用样式",
    cheatSheet: "单页速查表",
    glossary: "引用与学术写作术语表",
  },

  // /about
  aboutPage: {
    metaTitle: "关于 CitationEasy - 免费引用生成器",
    metaDescription:
      "CitationEasy 是一款免费的引用生成器，支持 {count} 种样式。由 Samuel Sergi 使用 Citation Style Language、Citation.js 和 Next.js 等开源工具构建。",
    backLink: "← 返回生成器",
    heading: "关于 CitationEasy",
    intro:
      "CitationEasy 是一款为学生、研究者和学术写作者打造的免费、开放访问的引用生成器。它支持 **{count} 种引用样式**，并提供不断扩充的写作工具、学习指南和参考资源——全部无需注册或订阅。",
    offerHeading: "我们提供什么",
    offer1:
      "[引用生成器](/)，支持 {count} 种样式，包括 APA 第 7 版、MLA 第 9 版、Chicago 第 18 版、Harvard、Vancouver、IEEE 及另外数千种样式",
    offer2:
      "[40 款免费写作与引用工具](/tools)——字数统计、可读性检测、DOI 校验、文献表按字母排序等",
    offer3:
      "[750 篇如何引用指南](/how-to-cite)，覆盖 6 种引用样式下的每一种主要文献类型",
    offer4: "[100 多篇学习指南](/guides)，涵盖引用最佳实践、学术写作和研究技能",
    offer5:
      "[样式比较工具](/compare)、[引用样式测验](/quiz)和[速查表](/cheat-sheets)，方便快速查阅",
    offer6:
      "7 种导出格式：纯文本、BibTeX、Word、编号列表、项目符号列表、RIS 和 CSV",
    openSourceHeading: "基于开源构建",
    openSourceIntro:
      "CitationEasy 的实现离不开许多优秀的开源项目和 API。我们相信应当对它们表示应有的感谢：",
    openSource1:
      "[Citation Style Language (CSL)](https://citationstyles.org/) —— 支撑我们 {count} 种引用样式的开放标准。CSL 由志愿者社区维护，Zotero、Mendeley、Papers 等文献管理工具都在使用它。样式定义来自 GitHub 上的 [CSL Styles Repository](https://github.com/citation-style-language/styles)。",
    openSource2:
      "[Citation.js](https://citation.js.org/) —— 按 CSL 样式处理并排版引用的 JavaScript 库，由 Lars Willighagen 创建。",
    openSource3:
      "[Open Library API](https://openlibrary.org/developers/api) —— 由互联网档案馆（Internet Archive）提供的免费图书检索 API，用于按书名或 ISBN 查找图书元数据。",
    openSource4:
      "[DOI Foundation](https://www.doi.org/) —— 用于解析和校验 DOI 的数字对象唯一标识符体系。",
    createdHeading: "创建者",
    createdBody:
      "CitationEasy 由 **Samuel Sergi** 创建并维护。目标很简单：提供一款真正免费、高质量的引用工具，不把功能藏在付费墙后，也不要求注册账号。",
    contactPrompt: "有疑问、反馈或建议？欢迎联系我们：",
    contactEmail: "邮箱：[sam@sammysergi.com](mailto:sam@sammysergi.com)",
    missionHeading: "我们的使命",
    mission1:
      "准确的引用不应该收费。许多引用工具宣称免费，实际却限制导出格式、限制引用数量，或诱导用户升级到付费套餐。CitationEasy 坚持完全免费——无需注册，没有限制，也没有付费版本。",
    mission2:
      "本站的每一项功能，从引用生成器到写作工具和学习指南，都对所有人免费开放。我们相信，维护学术诚信的工具应当让每一位学生和研究者都能使用，无论他们的预算如何。",
  },

  // /contact
  contactPage: {
    metaTitle: "联系我们",
    metaDescription:
      "联系 CitationEasy。对我们的免费引用生成器和写作工具有疑问、反馈或建议？欢迎与我们分享。",
    backLink: "← 返回生成器",
    heading: "联系我们",
    intro:
      "对 CitationEasy 有疑问、反馈或建议吗？欢迎与我们分享。无论你发现了 bug、有功能建议，还是在引用方面需要帮助，都请随时联系我们。",
    touchHeading: "联系方式",
    touchBody: "最好的联系方式是通过邮件。我们会尽量在 48 小时内回复。",
    emailLine: "**邮箱：** [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    commonHeading: "常见问题",
    commonIntro: "联系我们之前，你也许可以先在以下资源中找到答案：",
    resource1: "[引用指南](/guides) —— 各主要样式的分步引用说明",
    resource2:
      "[如何引用指南](/how-to-cite) —— 750 种文献类型与样式组合，附示例",
    resource3: "[速查表](/cheat-sheets) —— APA、MLA、Chicago 排版快速参考",
    resource4: "[写作工具](/tools) —— 字数统计、可读性检测、DOI 校验等",
    issueHeading: "反馈问题",
    issueIntro:
      "如果你发现了引用排版错误、功能故障或样式有误，请在联系我们时尽量提供详细信息：",
    issue1: "你当时使用的引用样式",
    issue2: "文献类型（图书、网站、期刊论文等）",
    issue3: "你期望的结果与实际生成结果的差异",
    issue4: "你使用的浏览器和设备（如相关）",
    issueOutro: "这有助于我们快速定位并修复问题。",
    aboutHeading: "关于 CitationEasy",
    aboutBody:
      "CitationEasy 是一款免费的引用生成器，支持一万余种引用样式，由 Samuel Sergi 创建，目标是让每个人都能轻松生成准确的引用。详情请参阅我们的[关于页面](/about)。",
  },

  // /ai — the agent and developer docs.
  //
  // The code samples, endpoint paths and MCP tool NAMES are deliberately absent: they are
  // the API's identifiers, served in English by the API itself, and a translated one
  // would document an endpoint that does not exist.
  aiPage: {
    metaTitle: "CitationEasy 面向 AI 助手与智能体 - API、MCP 服务器、llms.txt",
    metaDescription:
      "以编程方式使用 CitationEasy：一个免费、无需鉴权的 JSON API，支持 {count} 种样式的引用；一个面向智能体的 MCP 服务器；llms.txt；OpenAPI 规范；以及每个页面的 markdown 视图。",
    keyword1: "引用 API",
    keyword2: "MCP 服务器",
    keyword3: "引用生成器 API",
    keyword4: "llms.txt",
    keyword5: "OpenAPI",
    keyword6: "AI 智能体引用",
    keyword7: "免费引用 API",
    webApiDescription:
      "免费、无需鉴权的 JSON API，可根据 URL、DOI、ISBN、PubMed ID、arXiv ID 或 CSL-JSON 元数据，按 {count} 种 Citation Style Language 样式排版引用。",
    breadcrumb: "面向 AI 与智能体",
    heading: "CitationEasy 面向 AI 助手与智能体",
    intro:
      "本站的一切内容不仅对人类开放，也对软件开放：一个免费的 JSON API、一个 MCP 服务器、一份 OpenAPI 规范、一个 llms.txt 索引，以及全部 {count} 个页面的 markdown 视图。无需密钥，无需账号，也没有我们自设的速率限制。",
    whyHeading: "为什么调用 API，而不是自己手写引用",
    whyBody:
      "引用排版看起来像是一个文本生成问题，但其实不是。每种样式都编码了数百条关于作者姓名倒置、名字缩写、标题大小写、容器标点、版本说明和日期位置的规则，而这些规则之间的差异极其细微，仅凭记忆很容易出错。CitationEasy 使用 **citeproc-js** 根据官方 Citation Style Language 定义进行排版——这与 Zotero 和 Mendeley 所用的引擎相同——因此输出是确定性的，并且符合期刊投稿系统的要求。元数据来自 Crossref、Open Library 和 PubMed；不会凭空编造任何内容，上游缺失的字段在结果中也保持缺失，而不会用看似合理的猜测去填补。",
    generateHeading: "生成一条引用",
    generateBody:
      "在一次调用中发送 `inputs`（而非 `input`）即可处理最多 10 个来源，或者发送你已有的 CSL-JSON 元数据作为 `entry`，以完全跳过查找步骤。添加 `note: true` 可获得脚注形式。单个来源的失败只会体现在该来源上，因此一批请求不会整体失败。",
    mcpHeading: "通过 MCP 连接",
    mcpIntro:
      "位于 `/api/mcp` 的 Model Context Protocol 端点通过可流式传输的 HTTP 使用 JSON-RPC 2.0 协议，无需任何凭证。",
    mcpToolsHeading: "它提供的工具",
    endpointsHeading: "接口",
    colEndpoint: "接口",
    colWhat: "作用",
    endpointCite:
      "一次最多排版 10 个来源。接受 URL、DOI、ISBN、PubMed ID、arXiv ID、BibTeX/RIS/CSL-JSON 文本，或一个 CSL-JSON 条目对象。返回参考文献条目，也可选择返回文内引用和脚注。",
    endpointStyles: "按名称、引用格式类别、学科或地区搜索全部 {count} 种样式。",
    endpointStyleSlug:
      "单个样式的完整信息，附带由该样式自身 CSL 定义生成的真实排版示例。",
    endpointSearch: "对全站每个页面进行按相关度排序的搜索。",
    endpointPages: "以结构化条目形式提供完整的站点目录，可按页面类型筛选。",
    endpointHowToCite: "每一个“文献类型 × 样式”指导页面，可按任一维度筛选。",
    endpointTools: "每一款免费工具及其分类和关键词。",
    endpointGuides: "每一篇学习指南及其分类。",
    endpointConverters: "本站提供的每一种标识符与格式转换。",
    endpointComparisons: "并排的样式比较，链接到两种样式各自的记录。",
    endpointFields: "学科领域、学科分类和地区——用于回答“我应该用哪种样式？”。",
    endpointFaq: "经过审核的常见引用问题解答，可用 q 筛选。",
    endpointGlossary:
      "引用与学术写作术语及其完整定义——响应中已包含全文，因此无需再另行获取页面。",
    endpointIndex: "接口发现文档。如果你正在探索本站接口，从这里开始。",
    discoveryHeading: "发现文件",
    discoveryLlms: "为语言模型编写的本站精选索引。",
    discoveryLlmsFull: "本站发布的每一个页面，按类型分组，每行一条。",
    discoveryOpenapi: "整个公开 API 的 OpenAPI 3.1 描述。",
    discoveryMcp:
      "MCP 端点（可流式传输的 HTTP）。在浏览器中 GET 该地址即可看到说明。",
    discoveryWellKnownMcp: "供探测域名的智能体使用的 MCP 发现文档。",
    discoveryAiPlugin: "旧版插件清单，供仍在寻找它的框架使用。",
    discoveryMd: "本站任意页面的 markdown 视图。",
    discoverySitemap: "每一个可被索引的 URL。",
    catalogHeading: "目录中包含什么",
    catalogIntro:
      "下面的每个页面都可以通过 HTML、`/md/` 下的 markdown，以及 `/api/v1/pages` 返回的结构化条目三种方式访问。",
    catalogStyles:
      "**{count}** 种引用样式，每种都附有根据自身 CSL 定义生成的真实示例",
    catalogTools: "**{count}** 款免费浏览器工具",
    catalogGuides: "**{count}** 篇学习指南",
    catalogHowToCite: "**{count}** 个如何引用页面",
    catalogComparisons: "**{count}** 组样式比较",
    catalogConverters: "**{count}** 个转换工具",
    catalogFields: "**{count}** 个学科领域",
    catalogStyleLandings: "**{count}** 个主要样式专题页，附完整排版规则",
    faqHeading: "常见问题",
    faqFreeQ: "CitationEasy 的 API 是免费的吗？需要 API 密钥吗？",
    faqFreeA:
      "它是免费的，也不需要 API 密钥。/api/v1 下的每一个接口都无需鉴权，并且开放 CORS，因此你可以在浏览器、笔记本或智能体中直接调用，无需注册。我们不设自己的速率限制，但标识符查找会代理到 Crossref、Open Library 和 PubMed，这些服务有各自的合理使用限制——请使用 `inputs` 进行批量处理，而不要发起大量并行请求。",
    faqLlmQ: "这些引用是由语言模型生成的吗？",
    faqLlmA:
      "不是。排版使用 citeproc-js 根据官方 Citation Style Language 定义运行，与文献管理软件所用的引擎相同。相同的输入和样式总会生成相同的字符串。文献元数据来自 Crossref（DOI）、Open Library（ISBN）和 PubMed（PMID）——CitationEasy 不会凭空编造字段，因此上游缺失的信息在结果中也会缺失，而不是被猜测填补。",
    faqMcpQ: "如何通过 MCP 将智能体连接到 CitationEasy？",
    faqMcpA:
      "使用可流式传输的 HTTP 传输方式，将任意 Model Context Protocol 客户端指向 https://www.citationeasy.com/api/mcp。无需任何凭证。在 Claude Code 中：`claude mcp add --transport http citationeasy https://www.citationeasy.com/api/mcp`。该服务器提供的工具可用于生成引用、搜索 {count} 种样式、获取文献类型指导、站内搜索，以及以 markdown 形式读取任意页面。",
    faqStylesQ: "该 API 能生成哪些引用样式？",
    faqStylesA:
      "Citation Style Language 仓库中的全部 {count} 种样式，包括 APA 7、MLA 9、两种 Chicago 体系、Harvard、Vancouver、IEEE、AMA，以及数千种具体期刊样式。在 `style` 字段中传入样式的 slug 即可。像 “chicago”“harvard” 这样便于记忆的 slug 会解析到正确的 CSL 变体，其余样式可通过 /api/v1/styles?q= 查找。",
    faqLlmsTxtQ: "什么是 llms.txt？CitationEasy 的 llms.txt 在哪里？",
    faqLlmsTxtA:
      "llms.txt 是为语言模型（而非浏览器）编写的纯文本站点索引。CitationEasy 的索引位于 /llms.txt；列出本站每一个页面的完整版本位于 /llms-full.txt。",
    faqMarkdownQ: "可以以 markdown 而非 HTML 形式读取 CitationEasy 页面吗？",
    faqMarkdownA:
      "可以。在任意站内路径前加上 /md/ 前缀即可——例如 /md/citation-style/apa 或 /md/tools/word-counter。数据驱动型页面会返回完整内容，包括根据每种样式自身 CSL 定义生成的真实排版示例。手写文字类页面（指南、速查表）会返回一份摘要卡片，外加规范的 HTML 网址，因为它们的文字内容存在于页面本身之中。",
    faqReuseQ: "可以在我自己的产品中使用 CitationEasy 的输出结果吗？",
    faqReuseA:
      "可以。一条排版好的引用是关于某个来源的事实，你可以自由使用它。CSL 样式定义本身来自 Citation Style Language 项目，采用 CC BY-SA 3.0 协议发布。欢迎注明来自 CitationEasy，但并非强制要求。",
    missingHeading: "缺少了什么？",
    missingBody:
      "如果你的智能体需要一个本页未列出的接口、字段或格式，请[告诉我们](/contact)。想手动浏览？[从引用生成器开始](/)。",
  },

  // /quiz. The client component reads these as a prop rather than through a context:
  // it is one component, not the generator's nine, so there is nothing to drill through.
  quizPage: {
    metaTitle: "我应该用哪种引用样式？——免费样式测验",
    metaDescription:
      "回答几个关于你的学科、论文类型和偏好的简单问题，为你的论文找到最合适的引用样式。涵盖 APA、MLA、Chicago、Vancouver 等。",
    ogTitle: "我应该用哪种引用样式？—",
    ogDescription: "参加我们的免费测验，为你的论文找到最合适的引用样式。",
    breadcrumbHome: "CitationEasy",
    breadcrumbCurrent: "样式测验",
    backLink: "← 返回生成器",
    heading: "我应该用哪种引用样式？",
    intro: "回答几个简单问题，我们就会为你的论文推荐最合适的引用样式。",
    progress: "第 {current} 题，共 {total} 题",
    qDiscipline: "你的研究领域是什么？",
    optSocialScience: "心理学 / 社会科学",
    optHumanities: "文学 / 人文学科",
    optHistory: "历史学 / 艺术史",
    optMedical: "医学 / 护理学 / 健康科学",
    optEngineering: "工程学 / 计算机科学",
    optNaturalScience: "自然科学（生物学、化学）",
    optLaw: "法学",
    optBusiness: "商业 / 经济学",
    qPaperType: "你正在撰写哪种类型的论文？",
    optResearch: "研究论文或学位论文",
    optReview: "文献综述",
    optReport: "实验报告或技术报告",
    optEssay: "课程论文或课堂作业",
    optJournal: "期刊投稿",
    qRegion: "你所在的院校位于哪里？",
    optUs: "美国",
    optUk: "英国 / 澳大利亚",
    optCanada: "加拿大",
    optEurope: "欧洲（非英国）",
    optOther: "其他",
    qPreference: "你对引用格式有偏好吗？",
    optAuthorDate: "文内引用（作者，年份）",
    optNumeric: "编号引用 [1]",
    optFootnotes: "脚注",
    optNoPreference: "没有偏好",
    styleVancouver: "Vancouver",
    styleAma: "AMA（美国医学会）",
    styleIeee: "IEEE",
    styleBluebook: "Bluebook",
    styleCse: "CSE（科学编辑委员会）",
    styleChicagoNotes: "Chicago（注释与文献目录制）",
    styleMla: "MLA（第 9 版）",
    styleApa: "APA（第 7 版）",
    styleHarvard: "Harvard",
    reasonVancouverMedical:
      "Vancouver 是医学和健康科学期刊的标准样式，使用编号引用。",
    reasonAma: "AMA 广泛用于医学期刊，采用编号引用。",
    reasonIeee: "IEEE 是工程学、计算机科学和信息技术领域的标准引用样式。",
    reasonBluebook:
      "Bluebook 是法律写作中权威的引用样式。Chicago 样式是最接近的通用替代方案。",
    reasonCse: "CSE 是生物学和自然科学领域的标准样式。",
    reasonChicagoHistory:
      "Chicago 注释与文献目录制是历史学和艺术史领域的黄金标准，使用脚注进行引用。",
    reasonChicagoHumanities:
      "Chicago 注释与文献目录制以脚注为基础，非常适合人文学科。",
    reasonMla: "MLA 是文学、语言学科和人文学科领域的标准样式。",
    reasonApaBusiness: "APA 广泛用于商业、经济学和管理学领域。",
    reasonHarvard: "Harvard 引用法是英国和澳大利亚大学中最常用的样式。",
    reasonChicagoFootnotes:
      "Chicago 注释与文献目录制使用脚注，非常适合详细的引用。",
    reasonVancouverNumeric: "Vancouver 使用编号引用，让正文保持简洁易读。",
    reasonApaDefault:
      "APA 是社会科学、教育学及许多其他学科中使用最广泛的引用样式。",
    recommendLabel: "我们推荐",
    generateButton: "生成 {style} 引用",
    retakeButton: "重新测验",
    guidesLink: "浏览我们的引用指南获取更多帮助 →",
    previousButton: "← 上一题",
  },

  // /search — the server-rendered site search.
  searchPage: {
    metaTitle: "搜索 CitationEasy",
    metaDescription:
      "一次搜索 CitationEasy 上的所有页面：{count} 种引用样式、250 多个免费工具、指南、引用方法页、样式比较、转换工具和术语表。",
    keyword1: "搜索引用样式",
    keyword2: "查找引用指南",
    keyword3: "引用工具搜索",
    ogDescription:
      "一次搜索本站所有页面——样式、工具、指南、引用方法页、比较、转换工具和术语表词条。",
    ogImageSubtitle:
      "每一种引用样式、工具、指南、引用方法页、比较、转换工具和术语表词条——统一搜索。",
    ogKind: "搜索",
    breadcrumb: "搜索",
    heading: "搜索 CitationEasy",
    intro:
      "本站的每一种引用样式、工具、指南、引用方法页、比较、转换工具和术语表词条——统一搜索。",
    inputLabel: "搜索关键词",
    inputPlaceholder: "例如：用 APA 引用播客、悬挂缩进、DOI",
    submit: "搜索",
    browseHeading: "或按分类浏览",
    browseStyles: "引用样式",
    browseStylesNote: "{count} 种样式",
    browseTools: "免费工具",
    browseToolsNote: "{count} 个工具",
    browseHowTo: "如何引用任意来源",
    browseHowToNote: "{count} 个页面",
    browseGuides: "指南",
    browseGuidesNote: "{count} 篇指南",
    browseGlossary: "术语表",
    browseGlossaryNote: "{count} 个术语",
    browseComparisons: "样式比较",
    browseComparisonsNote: "{count} 组比较",
    browseConverters: "转换工具",
    browseConvertersNote: "{count} 个转换工具",
    browseCheatSheets: "速查表",
    browseCheatSheetsNote: "{count} 份速查表",
    facetsLabel: "按页面类型筛选",
    facetAll: "全部（{count}）",
    noMatches: "没有与“{query}”匹配的页面。",
    matchesOne: "找到 {count} 个与“{query}”匹配的页面。",
    matchesMany: "找到 {count} 个与“{query}”匹配的页面。",
    pagerLabel: "结果分页",
    pagerPrevious: "← 上一页",
    pagerNext: "下一页 →",
    pagerPosition: "第 {page} 页，共 {total} 页",
    paletteLoading: "正在搜索…",
    paletteError: "搜索暂时不可用，请重试。",
    paletteViewAll: "查看“{query}”的全部结果",
    apiNote:
      "正在开发相关应用？同一份索引也可以通过 JSON 获取：[/api/v1/search](/api/v1/search?q=podcast)，也可通过 MCP 获取——参见[面向智能体的文档](/ai)。",
  },

  // /cite-this-page. The six FAQ answers used to exist twice — once in the layout for the
  // FAQPage markup and once in the page for the visible list — which is exactly the drift
  // this dictionary removes: both now read the same keys.
  citeThisPage: {
    metaTitle: "引用本页 - 一键引用（书签工具 & Chrome 扩展）",
    metaDescription:
      "一键引用任何网页。把免费的“引用本页”书签工具拖到你的书签栏，或安装免费的 Chrome 扩展，通过工具栏按钮或右键菜单引用。同样适用于 Chromebook 和受限浏览器。",
    ogTitle: "引用本页书签工具 & Chrome 扩展",
    ogDescription:
      "一键为任意网页生成网站引用——支持拖放的书签工具，外加带右键引用功能的免费 Chrome 扩展。",
    breadcrumbRoot: "CitationEasy",
    breadcrumbSchemaCurrent: "引用本页书签工具",
    appName: "引用本页书签工具",
    appDescription:
      "一款免费的书签工具，一键引用你正在阅读的网页——它会打开 CitationEasy 并预填该页面的网址，然后以 APA、MLA、Chicago 及另外 {count} 种样式排版引用。",
    breadcrumbLabel: "面包屑导航",
    breadcrumbHome: "首页",
    breadcrumbCurrent: "引用本页书签工具",
    heading: "引用本页：书签工具 & Chrome 扩展",
    intro:
      "一键引用任意网页——无需注册。把下方的书签工具拖到你的书签栏（完全无需安装），或获取免费的 Chrome 扩展，通过工具栏按钮或右键菜单引用。",
    dragPrompt: "把这个按钮拖到你的书签栏：",
    buttonTitle: "把我拖到你的书签栏",
    buttonLabel: "📑 引用本页",
    dontClick: "不要在这里点击——请先把它拖到你的书签栏。",
    installHeading: "如何安装",
    step1Title: "显示书签栏",
    step1Body:
      "在大多数浏览器中按 Ctrl+Shift+B（Mac 上为 Cmd+Shift+B）可切换书签栏的显示，这样你才有地方放置按钮。",
    step2Title: "把按钮拖上去",
    step2Body:
      "点击并按住下方的“引用本页”按钮，把它拖到你的书签栏后再松开，它就会变成一个普通书签。",
    step3Title: "在任意页面点击它",
    step3Body:
      "阅读文章、新闻或期刊页面时，点击你新建的书签。CitationEasy 会打开并预填该页面的网址，引用即可复制。",
    manualHeading: "无法拖动？手动添加",
    manualBody: "新建一个书签，命名为“引用本页”，并把下面这段粘贴为网址：",
    howHeading: "工作原理",
    howBody:
      "该书签工具不会在你正在阅读的页面上运行任何代码，它只是把该页面的网址交给 CitationEasy，由我们的服务器获取并解析公开的页面元数据——与你手动把网址粘贴到生成器时走的是同一条可信路径。这样既安全，又避免了让早期页内书签工具失效的跨域限制。",
    extensionHeading: "更喜欢 Chrome 扩展？",
    extensionBody:
      "免费的 CitationEasy 扩展会在工具栏添加一个按钮，用于引用当前标签页，还提供右键菜单选项，可在不打开链接的情况下引用某个链接，或引用选中的 DOI 或 ISBN。和书签工具一样，它不会在你访问的页面上运行任何代码，只把网址交给 CitationEasy。它只需要 `activeTab` 和 `contextMenus` 权限，适用于 Chrome、Edge、Brave 及其他基于 Chromium 的浏览器。",
    downloadButton: "⬇ 下载 Chrome 扩展（.zip）",
    install1: "下载并解压扩展文件。",
    install2: "打开 `chrome://extensions`，开启右上角的**开发者模式**。",
    install3: "点击**加载已解压的扩展程序**，选择解压后的文件夹。",
    install4: "将 CitationEasy 按钮固定到工具栏，然后在任意页面点击即可引用。",
    faqHeading: "常见问题",
    faqWhatQ: "“引用本页”书签工具是什么？",
    faqWhatA:
      "这是一个拖到浏览器书签栏的小书签。在任意网页上点击它，会在新标签页打开 CitationEasy 并预填该页面的网址，让你一键获得排版好的引用——无需复制粘贴。",
    faqExtensionNeededQ: "需要安装扩展吗？",
    faqExtensionNeededA:
      "不需要。书签工具本质上只是一个书签，无需安装，也无需授予任何权限。它可在受限的学校 Chromebook 以及大多数禁用扩展的移动浏览器中使用。如果你更喜欢扩展，本页也提供免费的 Chrome 扩展。",
    faqChromeQ: "有 Chrome 扩展吗？",
    faqChromeA:
      "有。免费的 CitationEasy Chrome 扩展会在工具栏添加一个按钮，用于引用当前标签页，并提供右键菜单选项，可在不打开链接的情况下引用某个链接或选中的 DOI/ISBN。它不会在你访问的页面上运行任何代码，只需要 activeTab 和 contextMenus 权限。从本页下载后通过 chrome://extensions 加载即可，适用于 Chrome、Edge、Brave 及其他基于 Chromium 的浏览器。",
    faqAddQ: "如何添加书签工具？",
    faqAddA:
      "先确保书签栏可见，然后把本页上的“引用本页”按钮拖到书签栏上。如果无法拖动（例如在移动设备上），可以手动新建一个书签，并把书签工具的代码粘贴为网址。",
    faqSafeQ: "它安全吗？会读取我正在浏览的页面吗？",
    faqSafeA:
      "是的，它是安全的。书签工具不会在你正在阅读的页面上运行任何代码——它只会把该页面的网址发送给 CitationEasy，由我们的服务器获取并解析公开的页面元数据，与你手动粘贴网址时的处理方式完全相同。",
    faqOtherSourcesQ: "它适用于网站以外的其他来源吗？",
    faqOtherSourcesA:
      "书签工具本身面向网页，但它背后的链接也可以通过 ?source= 参数接受 DOI、ISBN、PMID 或 arXiv ID——因此一个可分享的“引用本页”链接同样适用于期刊论文和图书。",
    relatedHeading: "相关工具",
    relatedGenerator: "引用生成器",
    relatedHowToCite: "如何引用网站",
    relatedTools: "全部工具",
  },

  // /shared-bibliography — opens a bibliography that travelled inside a share link.
  sharedBibliographyPage: {
    metaTitle: "共享的参考文献列表 - 查看并导入共享的引用",
    metaDescription:
      "打开他人从 CitationEasy 分享给你的参考文献列表。查看已排版的引用、复制它们，或将整份列表导入到你自己的参考文献列表——免费、无需账号，数据全部保存在链接本身之中。",
    ogTitle: "共享的参考文献列表",
    ogDescription:
      "查看并导入共享的参考文献列表。引用数据保存在链接本身之中——不会存储在服务器上，链接永不过期。",
    breadcrumbRoot: "CitationEasy",
    breadcrumbLabel: "面包屑导航",
    breadcrumbHome: "首页",
    breadcrumbCurrent: "共享的参考文献列表",
    heading: "共享的参考文献列表",
    intro:
      "有人把这些引用分享给了你。复制它们，或一键将整份列表导入到你自己的 CitationEasy 参考文献列表中。",
    loading: "正在打开共享的参考文献列表…",
    copyAll: "全部复制",
    copied: "已复制",
    copyFailed: "复制失败",
    citationOne: "条引用",
    citationMany: "条引用",
    addAll: "全部添加到我的参考文献列表",
    added: "已添加 {count} 条到你的参考文献列表",
    skippedOne: "已跳过 {count} 条重复项",
    skippedMany: "已跳过 {count} 条重复项",
    openGenerator: "打开生成器",
    emptyHeading: "此链接中没有共享的参考文献列表",
    emptyBody:
      "本页面用于显示从 CitationEasy 分享出的参考文献列表。要分享你自己的列表，请在[引用生成器](/)中打开你的参考文献列表，选择**导出 → 复制分享链接**，然后把链接发给任何人。引用数据保存在链接本身之中——不会存储在服务器上，因此链接可以永久使用，你的数据也始终保密。",
    decodeError: "无法打开此分享链接。",
  },

  // The 19 source types the 15 detailed styles show worked examples for
  // (`DetailedStyleData.examples[].sourceType`). 75 rows, 19 distinct values — so this
  // is a vocabulary, not per-page copy, and it belongs here rather than repeated in
  // every locale's `src/content/style-pages/<locale>.json`.
  //
  // Two casings for the same reason `howToCitePage.refList*Lower` has two: the pages
  // print the label as a heading ("Book") and inside a sentence ("a sample APA entry
  // for a book"). Lower-casing the translated noun in code is wrong in German, where
  // every one of these is capitalised, so the lower form is a real key a translator
  // fills rather than a `.toLowerCase()` call.
  //
  // The citations themselves — `referenceEntry` and `inTextCitation` — are NOT here and
  // are not translatable anywhere: they are worked examples in a specific style's own
  // punctuation. See `src/lib/localized-content.ts`.
  sourceTypes: {
    book: "图书",
    bookLower: "图书",
    bookChapter: "图书章节",
    bookChapterLower: "图书章节",
    case: "案例",
    caseLower: "案例",
    conferencePaper: "会议论文",
    conferencePaperLower: "会议论文",
    courtCase: "法院判例",
    courtCaseLower: "法院判例",
    federalStatute: "联邦制定法",
    federalStatuteLower: "联邦制定法",
    governmentDocument: "政府文件",
    governmentDocumentLower: "政府文件",
    governmentReport: "政府报告",
    governmentReportLower: "政府报告",
    journalArticle: "期刊论文",
    journalArticleLower: "期刊论文",
    lawReviewArticle: "法律评论文章",
    lawReviewArticleLower: "法律评论文章",
    legislation: "法规",
    legislationLower: "法规",
    newspaperArticle: "报纸文章",
    newspaperArticleLower: "报纸文章",
    patent: "专利",
    patentLower: "专利",
    podcast: "播客",
    podcastLower: "播客",
    preprint: "预印本",
    preprintLower: "预印本",
    standard: "标准",
    standardLower: "标准",
    thesis: "学位论文",
    thesisLower: "学位论文",
    website: "网站",
    websiteLower: "网站",
    youtubeVideo: "YouTube 视频",
    youtubeVideoLower: "YouTube 视频",
  },

  // Chrome shared by the five per-style guide routes — /examples, /format,
  // /in-text-citation, /reference-page and /annotated-bibliography, 15 styles each.
  //
  // One section rather than five copies: the CTA, the back link and the cross-links
  // between the five pages are the same sentences on every one of them, and duplicating
  // them per route would be five identical translation units instead of one.
  styleGuidePages: {
    backToStyle: "返回 {style}",
    backToGenerator: "返回引用生成器",
    ctaHeading: "自动生成 {style} 引用",
    ctaButton: "立即用 {style} 引用 →",
    faqHeading: "常见问题",
    resourcesHeading: "更多资源",
    relatedHeading: "相关 {style} 指南",
    resourceGenerator: "免费引用生成器（{count} 种样式）",
    resourceTools: "免费写作工具",
    resourceGuides: "全部引用指南",
    resourceStyleGenerator: "{style} 引用生成器",
    resourceExamples: "{style} 引用示例",
    resourceFormat: "{style} 排版指南",
    resourceInText: "{style} 文内引用指南",
    resourceAnnotatedBib: "{style} 带注释文献表指南",
    resourceReferencePage: "{style} {list}指南",
    notFoundTitle: "未找到",
  },

  // /examples/<style> — the copy-ready worked examples for all 15 detailed styles.
  examplesPage: {
    metaTitle: "{style} 引用示例 - 全部文献类型 [2026]",
    metaDescription:
      "查看图书、期刊论文、网站等文献的 {style}（{edition}）格式引用示例。提供可直接复制的{list}条目和文内引用。",
    ogTitle: "{style} 引用示例",
    ogDescription: "每种常见文献类型的 {style} 条目，可直接复制使用。",
    ogImageTitle: "{style} 引用示例",
    ogKind: "{style} {edition}",
    keywordExamples: "{style} 引用示例",
    keywordReference: "{style} 参考文献示例",
    keywordInText: "{style} 文内引用示例",
    keywordList: "{style} {listLower}示例",
    keywordHowTo: "如何用 {style} 引用",
    breadcrumb: "{style} 引用示例",
    heading: "{style} 引用示例",
    lead: "查看 {count} 种常见文献类型的正确 {style}（{edition}）格式引用示例。每个示例都包含{listLower}条目和对应的文内引用。",
    entryLabel: "{list}条目：",
    inTextLabel: "文内引用：",
    ctaBody:
      "跳过手动排版。将 DOI、网址或 ISBN 粘贴到 CitationEasy，几秒钟内即可得到完美排版的 {style} 引用。",
    faqLookQ: "{style} 引用是什么样的？",
    faqLookA:
      "一条 {style} 引用包括一个包含完整文献信息的{listLower}条目和一个文内引用。具体格式因文献类型而异。例如，{style}（{edition}）中的图书引用包括作者、年份、标题和出版社。",
    faqHowManyQ: "有多少个 {style} 引用示例？",
    faqHowManyA:
      "CitationEasy 为 {count} 种文献类型提供 {style} 引用示例，包括{types}。每个示例都包含{listLower}条目和对应的文内引用。",
    faqCopyQ: "可以复制这些 {style} 引用示例吗？",
    faqCopyA:
      "可以，本页的 {style} 示例展示了每种文献类型的确切排版格式，你可以将它们用作自己引用的模板。如需自动排版，请使用我们的免费 {style} 引用生成器。",
  },

  // /format/<style> — font, margins, spacing, title page and headers. The VALUES those
  // labels take are per-style prose and live in the overlay, not here.
  formatPage: {
    metaTitle: "{style} 排版指南 - 论文格式规则 [2026]",
    metaDescription:
      "完整的 {style}（{edition}）论文排版指南。了解正确的字体、页边距、行距、标题页、页眉以及{listLower}的排版规则。",
    ogTitle: "{style} 论文格式",
    ogDescription: "{style} 论文的页边距、字体、行距和标题页要求。",
    ogImageTitle: "{style} 论文格式",
    ogImageSubtitle: "{margins} 页边距 · {spacing}",
    ogKind: "{style} {edition}",
    keywordFormat: "{style} 格式",
    keywordPaperFormat: "{style} 论文格式",
    keywordRules: "{style} 排版规则",
    keywordMargins: "{style} 页边距 字体 行距",
    keywordList: "{style} {listLower}格式",
    breadcrumb: "{style} 排版指南",
    heading: "{style} 排版指南",
    lead: "遵循这些 {style}（{edition}）论文排版规则，确保你的文档符合要求的标准。本指南涵盖字体、页边距、行距、标题页、页眉以及{listLower}的排版。",
    paperHeading: "论文排版",
    paperIntro: "以下是用 {style}（{edition}）样式撰写论文的核心排版要求。",
    labelFont: "字体",
    labelFontSize: "字号",
    labelMargins: "页边距",
    labelSpacing: "行距",
    labelTitlePage: "标题页",
    labelHeaders: "页眉/页码",
    listHeading: "{list}排版",
    listIntro:
      "你的{listLower}必须遵循以下规则，才能符合 {style} 样式的正确排版：",
    ctaBody:
      "让 CitationEasy 帮你完成排版。粘贴 DOI、网址或 ISBN，几秒钟内即可得到完美排版的 {style} 引用。",
    faqFontQ: "{style} 格式应该使用什么字体和字号？",
    faqFontA: "{style}（{edition}）要求使用{font}。标准字号为{fontSize}。",
    faqMarginsQ: "{style} 对页边距有什么要求？",
    faqMarginsA:
      "{style} 论文要求{margins}。这适用于每一页的上、下、左、右页边距。",
    faqTitlePageQ: "{style} 需要标题页吗？",
  },

  // /in-text-citation/<style> — the six in-text forms. The forms themselves
  // ("(Smith, 2024)") are per-style prose in the overlay.
  inTextCitationPage: {
    metaTitle: "{style} 文内引用指南 - 规则与示例 [2026]",
    metaDescription:
      "了解如何排版 {style}（{edition}）文内引用。涵盖单作者、多作者、直接引语、无作者和无日期情形的规则，并附示例。",
    ogTitle: "{style} 文内引用",
    ogDescription: "{style} 的每种文内引用形式，附示例。",
    ogImageTitle: "{style} 文内引用",
    ogImageSubtitle: "单作者 {singleAuthor} · 三位及以上作者 {threeOrMore}",
    ogKind: "{style} {edition}",
    keywordInText: "{style} 文内引用",
    keywordExamples: "{style} 文内引用示例",
    keywordParenthetical: "{style} 括号引用",
    keywordRules: "{style} 引用规则",
    keywordHowTo: "如何用 {style} 进行文内引用",
    breadcrumb: "{style} 文内引用",
    heading: "{style} 文内引用",
    lead: "通过这份完整指南掌握 {style}（{edition}）文内引用。了解每种情形下的正确格式，包括单作者与多作者、直接引语，以及信息缺失的文献。",
    rulesHeading: "文内引用规则",
    rulesIntro:
      "以下是撰写论文时会遇到的每种常见情形对应的 {style} 文内引用规则。",
    labelSingleAuthor: "单作者",
    labelTwoAuthors: "两位作者",
    labelThreeOrMore: "三位及以上作者",
    labelDirectQuote: "直接引语",
    labelNoAuthor: "无作者",
    labelNoDate: "无日期",
    examplesHeading: "快速示例",
    examplesIntro: "以下是不同文献类型的真实 {style} 文内引用示例：",
    ctaBody:
      "不必再为文内引用格式发愁。将 DOI、网址或 ISBN 粘贴到 CitationEasy，即可立即获得{listLower}条目和文内引用。",
    faqFormatQ: "如何排版 {style} 文内引用？",
    faqFormatA:
      "在 {style} 中，单作者使用格式 {singleAuthor}；两位作者使用 {twoAuthors}；三位及以上作者使用 {threeOrMore}。",
    faqQuoteQ: "如何在 {style} 中引用直接引语？",
    faqQuoteA:
      "在 {style}（{edition}）中，直接引语使用以下格式：{directQuote}。",
    faqMissingQ: "如果 {style} 中没有作者或没有日期怎么办？",
    faqMissingA:
      "在 {style} 中，如果没有作者，使用：{noAuthor}；如果没有日期，使用：{noDate}。",
  },

  // /reference-page/<style> — how to build the list at the back of the paper.
  //
  // The five `mistake*` strings carry `**bold**` inline markup because the lead-in of
  // each bullet is emphasised inside the sentence; they render through `InlineText`.
  referenceListPage: {
    metaTitle: "{style} {list}指南 - 排版规则 [2026]",
    metaDescription:
      "了解如何排版 {style}（{edition}）{listLower}。分步排版规则、示例，以及应避免的 {fullName} 常见错误。",
    ogTitle: "{style} {listLower}",
    ogDescription: "如何用 {style} 制作{list}。",
    ogImageTitle: "{style} {list}",
    ogKind: "{style} {edition}",
    breadcrumb: "如何排版 {style} {list}",
    heading: "如何排版 {style} {list}",
    lead: "{listLower}是任何按 {style}（{edition}）排版的学术论文中最重要的部分之一。它为读者提供了你引用的每份文献的完整信息，方便他们查找并核实你的参考文献。排版正确对学术可信度至关重要。本指南将介绍确切的规则，展示真实示例，并指出学生最常犯的错误。",
    rulesHeading: "排版规则",
    rulesIntro: "制作你的 {style} {listLower}时，请遵循以下规则：",
    exampleHeading: "{list}示例",
    exampleIntro: "以下是包含不同文献类型条目的 {style} {listLower}示例：",
    mistakesHeading: "应避免的常见错误",
    mistakesIntro:
      "以下是学生在制作 {style} {listLower}时最常犯的五种排版错误：",
    mistakeIndent:
      "**悬挂缩进缺失或不正确：** 每个条目的首行应顶格，后续所有行应缩进 0.5 英寸。请使用文字处理软件的段落设置，而不是按 Tab 键。",
    mistakeOrder:
      "**字母顺序或排列顺序不正确：** 条目必须按 {style} 的规则排列。按字母顺序排序时应忽略“A”“An”“The”等冠词。对于编号制样式，应按条目在正文中首次出现的顺序排列。",
    mistakeSpacing:
      "**行距不一致：** 请检查整份{listLower}的行距是否统一。混用单倍行距和双倍行距是一个容易被忽视的常见错误。",
    mistakeItalics:
      "**斜体使用错误：** 图书、期刊等独立作品的标题应使用斜体，但文章或章节标题不应使用斜体。请查阅 {style} 指南，确认具体哪些元素需要使用斜体。",
    mistakeLinks:
      "**DOI 与网址缺失或失效：** 如有 DOI，务必以可点击链接的形式提供。提交论文前，请测试每个网址，确保能正确链接到对应文献。",
    ctaHeading: "自动生成你的{list}",
    ctaBody:
      "跳过手动排版。将 DOI、网址或 ISBN 粘贴到 CitationEasy，几秒钟内即可得到可直接用于{listLower}的完美排版 {style} 引用。",
    faqWhatQ: "什么是 {style} {list}？",
    faqWhatA:
      "{style} {list}是论文末尾列出你在正文中引用的所有文献的部分，按 {fullName}（{edition}）的规范排版。它让读者能够查找并核实你引用的每份文献。",
    faqFormatQ: "如何排版 {style} {listLower}？",
    faqFormatA:
      "另起一页，标题写“{list}”，列出你在论文中引用的所有文献。遵循 {style} 关于悬挂缩进、行距、字母或编号顺序以及斜体的具体规则。",
    faqDifferenceQ: "{listLower}和参考书目有什么区别？",
    faqDifferenceA:
      "{listLower}只包含你在论文中直接引用的文献，而参考书目还可能包含你查阅过但未引用的文献。在 {style}（{edition}）中，论文末尾的这份清单称为“{list}”。",
  },

  // /annotated-bibliography/<style>. `whatTypes` carries `**bold**` inline markup.
  //
  // `exampleAnnotation` is a specimen annotation, not a citation: it is ordinary prose
  // about an imaginary source and translates like any other paragraph. The reference
  // entry printed above it does not — that comes from `DETAILED_STYLES` and stays as
  // the style wrote it.
  annotatedBibPage: {
    metaTitle: "{style} 带注释文献表指南与示例 [2026]",
    metaDescription:
      "了解如何用 {style}（{edition}）撰写带注释文献表。包含 {fullName} 带注释文献表的排版规则、示例条目和写作技巧。",
    ogTitle: "{style} 带注释文献表",
    ogDescription: "如何用 {style} 排版带注释文献表。",
    ogImageTitle: "{style} 带注释文献表",
    ogKind: "{style} {edition}",
    breadcrumb: "{style} 带注释文献表",
    heading: "{style} 带注释文献表",
    lead: "带注释文献表是一份文献引用清单，每条引用后都附有一段简短的描述性或评价性文字。在 {style} 格式（{edition}）中，每个条目先是一条排版正确的参考文献，随后是对该文献进行总结、评价或反思的注释。本指南将引导你了解制作一份精美的 {style} 带注释文献表所需的规则、排版方式和示例。",
    whatHeading: "什么是带注释文献表？",
    whatBody:
      "带注释文献表在标准{listLower}的基础上，为每条引用增加了一段简短的文字。这段注释能让读者了解该文献的内容、质量和相关性。教师常常布置带注释文献表的作业，帮助学生批判性地评估自己的文献，并展示每份文献如何服务于研究主题。",
    whatTypes:
      "注释通常有三种常见类型：概括文献内容的**描述性**注释、评估作品质量与可靠性的**评价性**注释，以及兼具两者的**综合型**注释。请以作业要求为准，确定应使用哪一种类型。",
    rulesHeading: "{style} 带注释文献表规则",
    rulesIntro: "用 {fullName} 排版带注释文献表时，请遵循以下规则：",
    exampleHeading: "带注释文献表条目示例",
    exampleIntro: "以下是一个{sourceType}的 {style} 带注释文献表条目示例：",
    exampleAnnotation:
      "该文献对主题进行了全面综述，提出了有实证依据支撑、论证充分的观点。作者广泛引用一手和二手资料，构建了具有说服力的论述。研究方法严谨，结论表述清晰，是理解该主题的宝贵资源。这份文献尤其有助于确立研究项目的理论框架，并提供了与文献表中其他文献相互补充的视角。",
    tipsHeading: "撰写 {style} 注释的技巧",
    tipSummarize:
      "先用两到三句话概括该文献，再补充你对其优点、局限性和相关性的评价。",
    tipTone:
      "全文使用第三人称和学术语气。除非教师另有要求，否则避免使用口语化表达或第一人称反思。",
    tipLength:
      "每条注释应控制在 100 到 200 词之间，力求简明，聚焦文献最重要的方面。",
    tipRelevance:
      "说明该文献如何契合你的整体研究，描述它贡献了怎样独特的视角或证据。",
    tipCitation:
      "撰写注释前，请确保每个条目的引用部分严格遵循 {fullName} 的排版规则。",
    ctaHeading: "自动生成你的带注释文献表",
    ctaBody:
      "使用 CitationEasy 带注释文献表生成器生成排版正确的 {style} 引用，然后添加你的注释。",
    ctaButton: "打开带注释文献表生成器 →",
    faqWhatQ: "{style} 中的带注释文献表是什么？",
    faqWhatA:
      "{style}（{edition}）中的带注释文献表是一份按 {fullName} 样式排版的引用清单，每条引用后都附有一段简短的文字，用于总结、评价或反思该文献。它有助于读者理解每份文献的相关性和质量。",
    faqLengthQ: "注释应该写多长？",
    faqLengthA:
      "注释通常为 100 到 200 词。根据所需分析的深度，有些作业可能要求更短或更长的注释。请务必以教师的具体要求为准。",
    faqIncludeQ: "注释应该包含哪些内容？",
    faqIncludeA:
      "注释应包括对文献主要论点或结论的简要概括、对文献可信度与实用性的评价，以及对该文献如何契合你的研究的反思。有些注释纯粹是描述性的，有些则包含批判性分析。",
  },

  guidePage: {
    byline: "作者：{author}",
    published: "发布于 {date}",
    updated: "更新于 {date}",
    readingTime: "other:预计阅读 {count} 分钟",
    wordCount: "other:{count} 个词",
  },

  // /guides hub, /guides/<category> index pages, and the chrome around each guide
  guideLibrary: {
    eyebrow: "指南库",
    browseByCategory: "按分类浏览",
    recentlyUpdated: "最近更新",
    allGuides: "全部指南",
    guideCount: "other:{count} 篇指南",
    updatedOn: "更新于 {date}",
    minRead: "other:阅读约 {count} 分钟",
    onThisPage: "本页内容",
    viewAllInCategory: "查看全部{category}指南",
    moreInCategory: "更多{category}指南",
    aboutThisGuide: "关于本指南",
    provenance: "作者：{author}。发布于 {published}；最近更新于 {updated}。",
    categoryIntroSuffix:
      "所有指南均免费，面向学生和研究者撰写，并标注日期，方便您了解最近一次核对的时间。",
    photoCredit: "图片：{author}，{source}（{license}）",
    imageAlt: "{category}指南的配图",
    imageSourceCommons: "维基共享资源",
    apaStyleName: "APA 格式",
    apaStyleDescription:
      "如何按 APA 第 7 版引用网站、期刊文章、书籍、视频等，每种来源都附参考文献列表和文内引用示例。",
    mlaStyleName: "MLA 格式",
    mlaStyleDescription:
      "如何按 MLA 第 9 版引用各类常见来源，从网页和期刊文章到诗歌、戏剧、电影和课程资料。",
    chicagoStyleName: "芝加哥格式",
    chicagoStyleDescription:
      "涵盖书籍、文章、档案、政府文件和媒体的注释-参考书目式与作者-日期式引用的《芝加哥格式手册》指南。",
    styleComparisonsName: "格式对比",
    styleComparisonsDescription:
      "APA、MLA、芝加哥、哈佛、温哥华、IEEE 等格式的并排对比，以及您的学科通常要求哪种格式的建议。",
    researchMethodsName: "研究方法",
    researchMethodsDescription:
      "如何规划研究论文、进行文献综述、做笔记、评估来源、避免抄袭以及编制参考书目。",
    academicWritingName: "学术写作",
    academicWritingDescription:
      "摘要、引言、结论、论点陈述、方法部分以及清晰学术文风的写作指南。",
    fieldSpecificName: "学科专属",
    fieldSpecificDescription:
      "针对各学科的引用指南，从心理学、历史学、文学到医学、工程、法学和护理学。",
    sourceEvaluationName: "来源评估",
    sourceEvaluationDescription:
      "如何判断可信度、区分学术来源与大众来源、使用 Google 学术和图书馆数据库，以及识别偏见。",
    citationHelpName: "引用帮助",
    citationHelpDescription:
      "疑难情况解答：缺少作者或日期、多位作者、PDF、AI 工具、法律案例，以及引用的导入与导出。",
  },

  common: {
    backToGuides: "← 返回指南",
    free: "免费",
    noSignup: "无需注册",
    backToHome: "返回参考文献生成器",
    inEnglish: "英文版",
    viewInEnglish: "查看英文版",
    availableInEnglish: "本页的英文版内容更为详尽。",
    otherLanguages: "其他语言",
    skipToContent: "跳转到正文",
    cookieNotice:
      "我们使用 Cookie 进行分析和广告投放，以保持 CitationEasy 免费。",
    privacyPolicy: "隐私政策",
    accept: "接受",
    decline: "拒绝",
    advertisement: "广告",
  },
  legalPage: {
    backLink: "← 返回生成器",
    effective: "生效日期：",
    updated: "最后更新：",
  },
};

export default zh;
